import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../../../core/api/apiClient';
import {
  ArrowLeft, GraduationCap, Briefcase, CheckCircle,
  Spinner, Robot, TrendUp, Coins, Star, Newspaper,
  TreeStructure, Warning, Info,
  Wrench, Globe, Heart, Certificate, FileText,
  BookOpen, Lightbulb, Trophy, UsersThree, Book, User,
  Link, ArrowClockwise, ArrowSquareOut, Bank, ThumbsUp, CalendarBlank, ChartBar, MapPin, FilePdf, PencilSimple, Database, Lock, LockOpen
} from '@phosphor-icons/react';
import { alumniService } from '../../../services/alumni.service';
import type { AlumniProfile } from '../../../services/alumni.service';
import { linkedinService } from '../../../services/linkedin.service';
import { Button } from '../../../components/ui/Button';
import { FakeProgressBar } from '../../../components/ui/FakeProgressBar';
import { ValsSection } from './ValsSection';
import { ResearchHistorySheet } from './ResearchHistorySheet';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { formatIndianCurrency, formatIndianNumber, formatToCr } from '../../../utils/currencyFormatter';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const safeStr = (val: unknown, fallback = 'N/A'): string => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'object') return JSON.stringify(val);
  const s = String(val).trim();
  return s === '' ? fallback : s;
};

const fmtDate = (d?: { year?: number; month?: number } | null): string => {
  if (!d) return 'Present';
  if (!d.year) return 'Present';
  if (d.month) {
    const m = new Date(d.year, d.month - 1).toLocaleString('default', { month: 'short' });
    return `${m} ${d.year}`;
  }
  return String(d.year);
};

const SOURCE_INFO_EXCLUDE = new Set([
  'csv_name', 'current_name', 'alumni_id', 'alumni_csr_id', 'donor_id', 'designation', 'company', 'degree', 'batch', 'department', 'work_city', 'linked_in', 'salesforce_url', 'salesforce_link', 'cumulative_giving_to_iitm', 'largest_gift_to_iitm', 'date_of_first_gift_to_iitm', 'date_of_last_gift_to_iitm', 'total_number_of_payments', 'smallest_gift', 'net_contribution', 'hni', 'daa', 'do_not_solicit', 'potential_donor', 'sales_person', 'last_touch', 'account_record_type',
  'amount_donated_in_the_fy21', 'amount_donated_in_the_fy22', 'amount_donated_in_the_fy23', 'amount_donated_in_the_fy24', 'amount_donated_in_the_fy25', 'amount_donated_in_the_fy26', 'scraped_at'
]);

const LINKEDIN_INFO_EXCLUDE = new Set([
  'name', 'about', 'avatar', 'photo', 'profilePic', 'experience', 'education', 
  'people_also_viewed', 'url', 'linkedin_url', 'city', 'location', 'country', 
  'scraped_at', 'certifications', 'honors', 'skills', 'languages', 'volunteering'
]);

const badgeColor = (val?: string): string => {
  if (!val) return 'bg-slate-100 text-slate-500';
  const v = val.toLowerCase();
  if (v === 'high') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (v === 'medium') return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (v === 'low') return 'bg-rose-50 text-rose-600 border border-rose-200';
  return 'bg-slate-100 text-slate-500 border border-slate-200';
};

const badgeDotColor = (val?: string): string => {
  if (!val) return 'bg-slate-300';
  const v = val.toLowerCase();
  if (v === 'high') return 'bg-emerald-500';
  if (v === 'medium') return 'bg-amber-500';
  if (v === 'low') return 'bg-rose-500';
  return 'bg-slate-300';
};

const trajectoryColor = (dir?: string): string => {
  if (!dir) return 'bg-slate-100 text-slate-500';
  if (dir.toLowerCase().includes('upward')) return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (dir.toLowerCase().includes('pivot') || dir.toLowerCase().includes('lateral')) return 'bg-sky-50 text-sky-700 border border-sky-200';
  if (dir.toLowerCase().includes('transit')) return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-slate-100 text-slate-600 border border-slate-200';
};

const scoreWidth = (val?: string | number): number => {
  if (val === undefined || val === null) return 0;
  const n = typeof val === 'number' ? val : parseFloat(String(val));
  if (isNaN(n)) return 0;
  return Math.min(100, Math.round(n <= 1 ? n * 100 : n));
};

const getInitials = (name: string): string => {
  if (!name || name === 'N/A') return '?';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
};

const parseMoney = (val: unknown): number => {
  if (typeof val === 'number') return val;
  if (typeof val !== 'string') return 0;
  const cleaned = val.replace(/[^0-9.-]/g, '');
  return Number(cleaned) || 0;
};

const formatCapacityValue = (val: unknown): string => {
  const raw = safeStr(val, 'Not Available');
  if (['n/a', 'not found', 'not available'].includes(raw.toLowerCase())) return 'Not Available';
  return formatToCr(raw);
};

const formatProfileCurrency = (val: unknown, fallback = 'N/A'): string => {
  return formatIndianCurrency(safeStr(val, fallback), fallback);
};

const formatProfileNumber = (val: unknown, fallback = 'N/A'): string => {
  return formatIndianNumber(safeStr(val, fallback), fallback);
};

const findValueByKeys = (source: unknown, keys: string[], maxDepth = 4): unknown => {
  if (!source || maxDepth < 0 || typeof source !== 'object') return undefined;

  const record = source as Record<string, unknown>;
  for (const key of keys) {
    const directValue = record[key];
    if (directValue !== undefined && directValue !== null && String(directValue).trim() !== '') {
      return directValue;
    }
  }

  for (const value of Object.values(record)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const nestedValue = findValueByKeys(value, keys, maxDepth - 1);
    if (nestedValue !== undefined && nestedValue !== null && String(nestedValue).trim() !== '') {
      return nestedValue;
    }
  }

  return undefined;
};

// ─── Shadow / elevation tiers ──────────────────────────────────────────────────
// Two deliberate tiers so the page has visual hierarchy instead of one flat shadow
// everywhere: SOFT for ordinary data cards, ELEVATED for hero / interactive cards.
const SHADOW_SOFT = 'shadow-[0_1px_3px_rgba(15,23,42,0.05)] hover:shadow-[0_6px_18px_-8px_rgba(15,23,42,0.10)]';
const SHADOW_ELEVATED = 'shadow-[0_18px_38px_-16px_rgba(15,23,42,0.20)] hover:shadow-[0_22px_44px_-14px_rgba(15,23,42,0.26)]';

// ─── Sub-components ───────────────────────────────────────────────────────────

function GlowBackdrop({ className = '' }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute -z-10 rounded-full bg-gradient-to-br from-accent/25 via-orange-300/10 to-transparent blur-3xl ${className}`}
    />
  );
}

function InfoChip({ label, value }: { label: string; value: string }) {
  return (
    <div className={`flex flex-col items-center bg-white border border-slate-200/70 rounded-2xl px-4 py-2.5 min-w-[90px] shrink-0 group hover:border-accent/40 hover:-translate-y-0.5 transition-all duration-200 ease-out ${SHADOW_SOFT}`}>
      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5 whitespace-nowrap">{label}</span>
      <span className="text-slate-900 font-extrabold text-sm text-center leading-snug">{value}</span>
    </div>
  );
}

function HeroStat({ label, value, sublabel, icon }: { label: string; value: string; sublabel?: string; icon?: React.ReactNode }) {
  return (
    <div className={`relative overflow-hidden rounded-3xl p-5 text-black ${SHADOW_ELEVATED}`}>
      <div aria-hidden className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-accent/30 blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">{label}</p>
          <p className="font-outfit font-black text-sm sm:text-2xl tracking-tight leading-none">{value}</p>
          {sublabel && <p className="text-slate-400 text-[11px] font-medium mt-2">{sublabel}</p>}
        </div>
        {icon && <div className="text-accent shrink-0 mt-0.5">{icon}</div>}
      </div>
    </div>
  );
}

interface ProfileHeroCardProps {
  name: string;
  alumniId: string;
  designation?: string;
  company?: string;
  location?: string;
  photoUrl?: string;
  skills: Array<{ name?: string; endorsements?: string }>;
  linkedinUrl?: string;
  salesforceUrl?: string;
  onBack: () => void;
}

function ProfileHeroCard({ name, alumniId, designation, company, location, photoUrl, skills, linkedinUrl, salesforceUrl, onBack }: ProfileHeroCardProps) {
  const topSkills = skills.filter(s => !!s.name).slice(0, 7);

  return (
    <div className={`relative rounded-3xl overflow-hidden border border-slate-200/60 bg-white ${SHADOW_ELEVATED}`}>
      {/* Banner — toned-down brand gradient with soft mesh blobs */}
      <div className="relative h-32 sm:h-40 overflow-hidden bg-gradient-to-br from-orange-100 via-orange-50 to-slate-100">
        <div aria-hidden className="absolute -top-10 -left-10 w-56 h-56 rounded-full bg-accent/25 blur-3xl" />
        <div aria-hidden className="absolute top-0 right-0 w-72 h-40 rounded-full bg-slate-300/30 blur-3xl" />
        <div aria-hidden className="absolute -bottom-16 left-1/3 w-64 h-48 rounded-full bg-accent-hover/15 blur-3xl" />

        {/* Quick-access actions, layered over the banner */}
        <div className="absolute top-4 right-4 flex items-center gap-2 no-print">
          {linkedinUrl && (
            <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" title="LinkedIn Profile"
               className="w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur border border-white/60 shadow-sm hover:bg-white transition-colors">
              <svg className="w-4 h-4 fill-current text-[#0A66C2]" viewBox="0 0 24 24">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
              </svg>
            </a>
          )}
          {salesforceUrl && (
            <a href={salesforceUrl} target="_blank" rel="noopener noreferrer" title="Salesforce CRM"
               className="w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur border border-white/60 shadow-sm hover:bg-white transition-colors">
              <svg className="w-4 h-4 fill-current text-[#00A1E0]" viewBox="0 0 24 24">
                <path d="M18.8 10c-.1 0-.3 0-.4.1C18 8.3 16.4 7 14.5 7c-1.7 0-3.1 1-3.8 2.4-.6-.5-1.3-.8-2.1-.8-1.5 0-2.8 1.1-3 2.6-.9-.3-1.8.1-2.2.9-.6.9-.4 2.1.4 2.8.1.1.3.1.4.1h14.6c.1 0 .3 0 .4-.1.8-.7 1-1.9.4-2.8-.4-.8-1.3-1.2-2.2-.9-.2-1.5-1.5-2.6-3-2.6z"/>
              </svg>
            </a>
          )}
          <button onClick={onBack} title="Back to Directory"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur border border-white/60 shadow-sm hover:bg-white transition-colors text-slate-700">
            <ArrowLeft size={15} weight="bold" />
          </button>
        </div>
      </div>

      <div className="relative px-6 sm:px-8 pb-6">
        <div className="-mt-12 sm:-mt-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
          {/* Avatar + identity */}
          <div className="flex items-end gap-4">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl ring-4 ring-white shadow-lg bg-gradient-to-br from-accent to-accent-hover flex items-center justify-center text-white font-black text-2xl shrink-0 overflow-hidden">
              {photoUrl ? (
                <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
              ) : (
                getInitials(name)
              )}
            </div>
            <div className="pb-1 min-w-0">
              <h1 className="font-outfit font-extrabold text-2xl sm:text-3xl text-slate-900 tracking-tight leading-tight truncate">{name}</h1>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                {designation && designation !== 'N/A' && (
                  <span className="text-slate-600 font-semibold text-sm">{designation}{company && company !== 'N/A' ? ` · ${company}` : ''}</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                {location && location !== 'N/A' && (
                  <span className="text-slate-400 text-xs font-medium flex items-center gap-1">
                    <MapPin size={12} weight="fill" /> {location}
                  </span>
                )}
                <span className="text-slate-400 text-xs font-mono font-bold">{alumniId}</span>
              </div>
            </div>
          </div>

          {/* Current role pill */}
          {designation && designation !== 'N/A' && (
            <div className="pb-1 sm:text-right shrink-0">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 sm:justify-end">
                <Briefcase size={12} weight="fill" /> Current Role
              </span>
              <span className="inline-block mt-1.5 text-xs font-bold text-slate-700 bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-full">
                {designation}
              </span>
            </div>
          )}
        </div>

        {/* Skills row */}
        {topSkills.length > 0 && (
          <div className="mt-6 pt-5 border-t border-slate-100">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-2.5">
              <Star size={13} weight="fill" className="text-amber-400" /> Skills
            </span>
            <div className="flex flex-wrap gap-2">
              {topSkills.map((s, i) => (
                <span key={i} className="text-xs font-semibold text-slate-700 bg-orange-50/70 border border-orange-100 px-3 py-1.5 rounded-full hover:border-accent/40 transition-colors">
                  {s.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionCard({ children, className = '', elevated = false }: { children: React.ReactNode; className?: string; elevated?: boolean }) {
  return (
    <div className={`bg-white border border-slate-200/60 rounded-2xl p-6 transition-shadow duration-300 ${elevated ? SHADOW_ELEVATED : SHADOW_SOFT} ${className}`}>
      {children}
    </div>
  );
}

function SectionTitle({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <h3 className="font-sans font-bold text-lg text-slate-800 mb-5 flex items-center gap-2">
      {icon}
      {label}
    </h3>
  );
}


function Badge({ val }: { val?: string }) {
  if (!val || val === 'Not found') return <span className="text-slate-400 font-medium text-sm">N/A</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${badgeColor(val)}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${badgeDotColor(val)}`} />
      {val}
    </span>
  );
}

// ─── Experience/Education Timeline Item ───────────────────────────────────────

function TimelineItem({ item, type }: { item: Record<string, unknown>; type: 'experience' | 'education' }) {
  const title = (item.title || item.degree || item.field_of_study || 'Unknown Role') as string;
  const org = (item.company || item.school || 'Unknown') as string;
  const initials = org.split(/\s+/).slice(0, 2).map((w: string) => w[0] ?? '').join('').toUpperCase() || '?';

  let startStr = '';
  let endStr = 'Present';

  if (item.starts_at && typeof item.starts_at === 'object') {
    startStr = fmtDate(item.starts_at as { year?: number; month?: number });
  } else if (typeof item.start_date === 'string') {
    startStr = item.start_date as string;
  }

  if (item.ends_at && typeof item.ends_at === 'object') {
    endStr = fmtDate(item.ends_at as { year?: number; month?: number });
  } else if (typeof item.end_date === 'string' && (item.end_date as string).trim() !== '') {
    endStr = item.end_date as string;
  }

  const dateStr = startStr ? `${startStr} – ${endStr}` : endStr;
  const desc = item.description as string | undefined;
  const loc = item.location as string | undefined;

  return (
    <div className="relative pl-8 pb-5 group last:pb-1 flex items-start gap-3">
      {/* Vertical connector line */}
      <div className="absolute left-4 top-2.5 bottom-0 w-[2px] bg-slate-100 group-last:hidden" />

      {/* Timeline ring */}
      <div className={`absolute left-2.5 top-1.5 w-3.5 h-3.5 rounded-full bg-white border-2 shrink-0 z-10 ${
        type === 'experience' ? 'border-sky-500' : 'border-emerald-500'
      }`} />

      {/* Initials / Logo Box */}
      <div className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-[10px] font-black text-white shadow-sm z-10 ${
        type === 'experience' ? 'bg-gradient-to-br from-slate-700 to-slate-900' : 'bg-gradient-to-br from-accent to-accent-hover'
      }`}>
        {initials}
      </div>

      {/* Text Details */}
      <div className="min-w-0 flex-1">
        <p className="font-bold text-slate-800 text-sm leading-snug">{title}</p>
        <p className="text-slate-500 font-medium text-xs mt-0.5">{org}</p>
        <p className="text-slate-400 text-[10px] font-semibold mt-0.5">{dateStr}</p>
        {loc && <p className="text-slate-400 text-[10px] font-medium mt-0.5 flex items-center gap-1"> {loc}</p>}
        {desc && (
          <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed line-clamp-3">{desc}</p>
        )}
      </div>
    </div>
  );
}

// ─── Motivation Score Row ─────────────────────────────────────────────────────

// ─── Additional LinkedIn Info Sub-components ──────────────────────────────────

interface SkillsSectionProps {
  skills: Array<{ name?: string; endorsements?: string }>;
}

function SkillsSection({ skills }: SkillsSectionProps) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <Wrench size={22} className="text-amber-500" weight="duotone" />
        SKILLS & TOP COMPETENCIES
      </h3>
      <div className="flex flex-wrap gap-2.5">
        {skills.map((skill, i) => {
          const name = skill.name || '';
          const end = skill.endorsements;
          if (!name) return null;
          return (
            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200/80 rounded-xl text-xs font-semibold text-slate-700 hover:border-accent hover:text-accent transition-colors duration-200">
              {name}
              {end && (
                <span className="text-[10px] font-extrabold px-1.5 py-0.2 bg-slate-200/60 text-slate-500 rounded-md">
                  {end}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </SectionCard>
  );
}

interface LanguagesSectionProps {
  languages: Array<{ title?: string; subtitle?: string }>;
}

function LanguagesSection({ languages }: LanguagesSectionProps) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <Globe size={22} className="text-sky-500" weight="duotone" />
        LANGUAGES
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {languages.map((lang, i) => {
          const title = lang.title || '';
          const subtitle = lang.subtitle;
          if (!title) return null;
          return (
            <div key={i} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
              <span className="text-sm font-bold text-slate-700">{title}</span>
              {subtitle && <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">{subtitle}</span>}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

interface VolunteeringSectionProps {
  volunteering: Array<{
    role?: string;
    duration?: string;
    startDate?: string;
    endDate?: string;
    organizationName?: string;
    organizationLinkedinUrl?: string;
    cause?: string;
  }>;
}

function VolunteeringSection({ volunteering }: VolunteeringSectionProps) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <Heart size={22} className="text-rose-500" weight="duotone" />
        VOLUNTEER EXPERIENCE
      </h3>
      <div className="space-y-4">
        {volunteering.map((item, i) => {
          const role = item.role;
          const org = item.organizationName;
          const duration = item.duration;
          const start = item.startDate;
          const end = item.endDate;
          const orgUrl = item.organizationLinkedinUrl;
          const cause = item.cause;

          let dateStr = duration || '';
          if (!dateStr && (start || end)) {
            dateStr = start ? `${start} – ${end || 'Present'}` : (end || '');
          }

          if (!role && !org) return null;

          return (
            <div key={i} className="flex gap-3 items-start border-b border-slate-100 pb-3 last:border-0 last:pb-0">
              <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center text-rose-500 shrink-0 font-extrabold text-sm">
                ♥
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-800 text-sm leading-snug">{role || 'Volunteer'}</p>
                <p className="text-slate-500 font-medium text-xs mt-0.5">
                  {orgUrl ? (
                    <a href={orgUrl} target="_blank" rel="noopener noreferrer" className="hover:text-accent underline">
                      {org || 'Organization'}
                    </a>
                  ) : (
                    org || 'Organization'
                  )}
                </p>
                {dateStr && <p className="text-slate-400 text-[10px] font-semibold mt-0.5">{dateStr}</p>}
                {cause && <p className="text-slate-400 text-[10px] font-medium mt-0.5">Cause: {cause}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

interface CertificationsSectionProps {
  certifications: Array<{
    title?: string;
    subtitle?: string;
    meta?: string;
    credential_url?: string;
    credential_id?: string;
  }>;
}

function CertificationsSection({ certifications }: { certifications: CertificationsSectionProps['certifications'] }) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <Certificate size={22} className="text-violet-500" weight="duotone" />
        CERTIFICATIONS
      </h3>
      <div className="space-y-4">
        {certifications.map((cert, i) => {
          const title = cert.title || '';
          const sub = cert.subtitle;
          const url = cert.credential_url;
          const id = cert.credential_id;
          const meta = cert.meta;
          if (!title) return null;

          return (
            <div key={i} className="flex gap-3 items-start border-b border-slate-100 pb-3 last:border-0 last:pb-0">
              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600 shrink-0">
                <Certificate size={18} weight="fill" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-800 text-sm leading-snug">{title}</p>
                {sub && <p className="text-slate-500 font-medium text-xs mt-0.5">{sub}</p>}
                {meta && <p className="text-slate-400 text-[10px] font-medium mt-0.5">{meta}</p>}
                {id && !meta && <p className="text-slate-400 text-[10px] font-medium mt-0.5">ID: {id}</p>}
                {url && (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-bold text-accent hover:underline mt-1">
                    See Credential ➔
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

interface ProjectsSectionProps {
  projects: Array<{
    title?: string;
    start_date?: string;
    end_date?: string;
    description?: string;
  }>;
}

function ProjectsSection({ projects }: ProjectsSectionProps) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <FileText size={22} className="text-emerald-500" weight="duotone" />
        PROJECTS
      </h3>
      <div className="space-y-4">
        {projects.map((proj, i) => {
          const title = proj.title || '';
          const start = proj.start_date;
          const end = proj.end_date;
          const desc = proj.description;
          const dateStr = start ? `${start} – ${end || 'Present'}` : (end || '');
          if (!title) return null;

          return (
            <div key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
              <p className="font-bold text-slate-800 text-sm leading-snug">{title}</p>
              {dateStr && <p className="text-slate-400 text-[10px] font-semibold mt-0.5">{dateStr}</p>}
              {desc && <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">{desc}</p>}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

interface PublicationsSectionProps {
  publications: Array<{
    title?: string;
    description?: string;
    date?: string;
    publisher?: string;
    url?: string;
  }>;
}

function PublicationsSection({ publications }: PublicationsSectionProps) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <BookOpen size={22} className="text-indigo-500" weight="duotone" />
        PUBLICATIONS
      </h3>
      <div className="space-y-4">
        {publications.map((pub, i) => {
          const title = pub.title || '';
          const desc = pub.description;
          const date = pub.date;
          const publisher = pub.publisher;
          const url = pub.url;
          if (!title) return null;

          return (
            <div key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
              <p className="font-bold text-slate-800 text-sm leading-snug">{title}</p>
              {(publisher || date) && (
                <p className="text-slate-400 text-[10px] font-semibold mt-0.5">
                  {publisher && <span>{publisher}</span>}
                  {publisher && date && <span> · </span>}
                  {date && <span>{date}</span>}
                </p>
              )}
              {desc && <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">{desc}</p>}
              {url && (
                <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-bold text-accent hover:underline mt-1">
                  View Publication ➔
                </a>
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

interface PatentsSectionProps {
  patents: Array<{
    title?: string;
    date_issued?: string;
    patents_id?: string;
    description?: string;
  }>;
}

function PatentsSection({ patents }: PatentsSectionProps) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <Lightbulb size={22} className="text-yellow-500" weight="duotone" />
        PATENTS
      </h3>
      <div className="space-y-4">
        {patents.map((pat, i) => {
          const title = pat.title || '';
          const id = pat.patents_id;
          const date = pat.date_issued;
          const desc = pat.description;
          if (!title) return null;

          return (
            <div key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
              <p className="font-bold text-slate-800 text-sm leading-snug">{title}</p>
              {(id || date) && (
                <p className="text-slate-400 text-[10px] font-semibold mt-0.5">
                  {id && <span>Patent ID: {id}</span>}
                  {id && date && <span> · </span>}
                  {date && <span>Issued: {date}</span>}
                </p>
              )}
              {desc && <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">{desc}</p>}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

interface HonorsSectionProps {
  honors: Array<{
    title?: string;
    publication?: string;
    date?: string;
    description?: string;
    issuedBy?: string;
    issuedAt?: string;
  }>;
}

function HonorsSection({ honors }: HonorsSectionProps) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <Trophy size={22} className="text-amber-500" weight="duotone" />
        HONORS & AWARDS
      </h3>
      <div className="space-y-4">
        {honors.map((item, i) => {
          const title = item.title || '';
          const pub = item.publication || item.issuedBy || '';
          const date = item.date || item.issuedAt || '';
          const desc = item.description;
          if (!title) return null;

          let dateStr = date;
          if (date && date.includes('T')) {
            try {
              dateStr = new Date(date).getFullYear().toString();
            } catch {
              dateStr = date;
            }
          }

          return (
            <div key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
              <p className="font-bold text-slate-800 text-sm leading-snug">{title}</p>
              {(pub || dateStr) && (
                <p className="text-slate-400 text-[10px] font-semibold mt-0.5">
                  {pub && <span>{pub}</span>}
                  {pub && dateStr && <span> · </span>}
                  {dateStr && <span>{dateStr}</span>}
                </p>
              )}
              {desc && <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">{desc}</p>}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

interface OrganizationsSectionProps {
  organizations: Array<{
    title?: string;
    membership_type?: string;
    start_date?: string;
    end_date?: string;
  }>;
}

function OrganizationsSection({ organizations }: OrganizationsSectionProps) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <UsersThree size={22} className="text-teal-500" weight="duotone" />
        ORGANIZATIONS
      </h3>
      <div className="space-y-4">
        {organizations.map((org, i) => {
          const title = org.title || '';
          const role = org.membership_type;
          const start = org.start_date;
          const end = org.end_date;
          const dateStr = start ? `${start} – ${end || 'Present'}` : (end || '');
          if (!title) return null;

          return (
            <div key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
              <p className="font-bold text-slate-800 text-sm leading-snug">{title}</p>
              {role && role !== '-' && <p className="text-slate-500 font-medium text-xs mt-0.5">Role: {role}</p>}
              {dateStr && <p className="text-slate-400 text-[10px] font-semibold mt-0.5">{dateStr}</p>}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

interface CoursesSectionProps {
  courses: Array<{ title?: string; subtitle?: string }>;
}

function CoursesSection({ courses }: CoursesSectionProps) {
  return (
    <SectionCard>
      <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
        <Book size={22} className="text-blue-500" weight="duotone" />
        COURSES
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {courses.map((course, i) => {
          const title = course.title || '';
          const subtitle = course.subtitle;
          if (!title) return null;
          return (
            <div key={i} className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex flex-col justify-center">
              <span className="text-sm font-bold text-slate-700 leading-snug">{title}</span>
              {subtitle && subtitle !== '-' && (
                <span className="text-[10px] font-bold text-slate-400 mt-0.5">{subtitle}</span>
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

function SimilarDonorSkeleton() {
  return (
    <div className="bg-slate-50 border border-slate-200/40 rounded-2xl p-5 animate-pulse space-y-4">
      <div className="flex justify-between items-start gap-2">
        <div className="h-4 bg-slate-200 rounded w-1/2" />
        <div className="h-4 bg-slate-200 rounded-full w-16" />
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-slate-200 rounded w-3/4" />
        <div className="h-2 bg-slate-200 rounded w-1/2" />
      </div>
      <div className="border-t border-slate-200/40 pt-3 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-1">
            <div className="flex justify-between">
              <div className="h-2.5 bg-slate-200 rounded w-12" />
              <div className="h-2.5 bg-slate-200 rounded w-6" />
            </div>
            <div className="w-full bg-slate-200 rounded-full h-1" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function AlumniDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isAdmin = localStorage.getItem('role') === 'admin';

  /**
   * Pop the history entry instead of pushing /alumni, so the list we came from keeps its
   * search, filters, page and scroll — those live in the URL of that entry. A 'default'
   * key means this profile was opened directly, so there is nothing to pop back to.
   */
  const goBack = () => {
    if (routerLocation.key !== 'default') {
      navigate(-1);
    } else if ((routerLocation.state as any)?.from === 'constellation') {
      navigate('/');
    } else {
      navigate('/alumni');
    }
  };

  const [alumni, setAlumni] = useState<AlumniProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isResearching, setIsResearching] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [researchProgress, setResearchProgress] = useState<string>('');
  const [isScraping, setIsScraping] = useState(false);
  const [scrapeSuccess, setScrapeSuccess] = useState(false);
  
  const [isEditingLinkedin, setIsEditingLinkedin] = useState(false);
  const [editLinkedinValue, setEditLinkedinValue] = useState('');
  const [isSavingLinkedin, setIsSavingLinkedin] = useState(false);
  const [isClearingFlag, setIsClearingFlag] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isPrinting, setIsPrinting] = useState(false);

  const [similarAlumni, setSimilarAlumni] = useState<AlumniProfile[]>([]);
  const [similarLimit, setSimilarLimit] = useState(5);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    alumniService.getAlumniById(id)
      .then(setAlumni)
      .catch((err: Error) => setError(err.message || 'Failed to load alumni'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setSimilarLimit(5);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoadingSimilar(true);
    alumniService.getSimilarAlumni(id, similarLimit)
      .then((res) => {
        if (Array.isArray(res)) {
          setSimilarAlumni(res);
        } else {
          setSimilarAlumni([]);
        }
      })
      .catch((err) => {
        console.error("Failed to load similar alumni:", err);
        setSimilarAlumni([]);
      })
      .finally(() => setLoadingSimilar(false));
  }, [id, similarLimit]);

  const startResearch = async (isRetry = false, forceRefresh = false) => {
    if (!id) return;
    setIsResearching(true);
    setResearchProgress('Starting AI research...');

    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    const host = window.location.host;
    const basePath = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/$/, '');
    let sseUrl = import.meta.env.DEV
      ? `${protocol}//localhost:8000/api/dashboard/alumni/${id}/research/stream`
      : `${protocol}//${host}${basePath}/api/dashboard/alumni/${id}/research/stream`;
      
    if (forceRefresh) {
      sseUrl += '?force_refresh=true';
    }
      
    const eventSource = new EventSource(sseUrl, { withCredentials: true });
    let completed = false;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'progress') {
          setResearchProgress(data.message);
        } else if (data.type === 'result') {
          completed = true;
          if (data.alumni) {
            setAlumni(data.alumni);
          } else {
            alumniService.getAlumniById(id).then(setAlumni).catch(console.error);
          }
          setIsResearching(false);
          eventSource.close();
        } else if (data.type === 'error') {
          completed = true;
          alert(`Research failed: ${data.message}`);
          setIsResearching(false);
          eventSource.close();
        }
      } catch (err) {
        console.error("Error parsing SSE data", err);
      }
    };

    eventSource.onerror = async () => {
      eventSource.close();
      if (!completed && !isRetry) {
        try {
          await axios.post(`${getApiBaseUrl()}/auth/refresh`, {}, { withCredentials: true });
          startResearch(true, forceRefresh);
        } catch (refreshErr) {
          localStorage.removeItem('token');
          localStorage.removeItem('name');
          localStorage.removeItem('role');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          alert('Authentication expired. Please log in again.');
          setIsResearching(false);
        }
      } else if (!completed && isRetry) {
        alert('Connection to research service lost');
        setIsResearching(false);
      }
    };
  };

  const handleResearch = () => {
    startResearch(false, true);
  };

  // ─── PDF Download ─────────────────────────────────────────────────────────
  const pdfRootRef = useRef<HTMLDivElement | null>(null);

  const handleDownloadPdf = () => {
    // Just flip the flag — a useEffect watches the ref and fires the capture
    setIsPrinting(true);
  };

  useEffect(() => {
    if (!isPrinting) return;
    const el = pdfRootRef.current;
    if (!el) return; // wait for next render if not mounted yet

    let cancelled = false;

    const generate = async () => {
      try {
        const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
          import('html2canvas-pro'),
          import('jspdf'),
        ]);

        if (cancelled) return;

        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: 900,
          height: el.scrollHeight,
          scrollX: 0,
          scrollY: 0,

          onclone: (_doc, clonedEl) => {
            const style = clonedEl.ownerDocument.createElement('style');
            style.textContent = `
              :root, *, *::before, *::after {
                /* slate */
                --color-slate-50:  #f8fafc; --color-slate-100: #f1f5f9;
                --color-slate-200: #e2e8f0; --color-slate-300: #cbd5e1;
                --color-slate-400: #94a3b8; --color-slate-500: #64748b;
                --color-slate-600: #475569; --color-slate-700: #334155;
                --color-slate-800: #1e293b; --color-slate-900: #0f172a;
                --color-slate-950: #020617;
                /* rose */
                --color-rose-50:  #fff1f2; --color-rose-100: #ffe4e6;
                --color-rose-200: #fecdd3; --color-rose-300: #fda4af;
                --color-rose-400: #fb7185; --color-rose-500: #f43f5e;
                --color-rose-600: #e11d48; --color-rose-700: #be123c;
                --color-rose-800: #9f1239; --color-rose-900: #881337;
                /* emerald */
                --color-emerald-50:  #ecfdf5; --color-emerald-100: #d1fae5;
                --color-emerald-200: #a7f3d0; --color-emerald-400: #34d399;
                --color-emerald-500: #10b981; --color-emerald-600: #059669;
                --color-emerald-700: #047857; --color-emerald-800: #065f46;
                /* amber */
                --color-amber-50:  #fffbeb; --color-amber-100: #fef3c7;
                --color-amber-200: #fde68a; --color-amber-300: #fcd34d;
                --color-amber-400: #fbbf24; --color-amber-500: #f59e0b;
                --color-amber-600: #d97706; --color-amber-700: #b45309;
                --color-amber-800: #92400e; --color-amber-900: #78350f;
                /* sky */
                --color-sky-50:  #f0f9ff; --color-sky-100: #e0f2fe;
                --color-sky-200: #bae6fd; --color-sky-300: #7dd3fc;
                --color-sky-400: #38bdf8; --color-sky-500: #0ea5e9;
                --color-sky-600: #0284c7; --color-sky-700: #0369a1;
                /* violet */
                --color-violet-50:  #f5f3ff; --color-violet-100: #ede9fe;
                --color-violet-200: #ddd6fe; --color-violet-500: #8b5cf6;
                --color-violet-600: #7c3aed; --color-violet-700: #6d28d9;
                /* orange */
                --color-orange-50:  #fff7ed; --color-orange-100: #ffedd5;
                --color-orange-200: #fed7aa; --color-orange-400: #fb923c;
                --color-orange-500: #f97316; --color-orange-600: #ea580c;
                --color-orange-700: #c2410c;
                /* indigo */
                --color-indigo-50:  #eef2ff; --color-indigo-100: #e0e7ff;
                --color-indigo-500: #6366f1; --color-indigo-600: #4f46e5;
                /* white / black */
                --color-white: #ffffff; --color-black: #000000;
                /* project accent (brand orange) */
                --color-accent:        #f97316;
                --color-accent-hover:  #ea580c;
                --color-accent-active: #c2410c;
                --color-accent-light:  #fff7ed;
                --color-accent-muted:  #fdba74;
              }
            `;
            clonedEl.ownerDocument.head.appendChild(style);
          },
        });

        if (cancelled) return;

        const imgPxW = canvas.width;
        const imgPxH = canvas.height;

        if (imgPxW === 0 || imgPxH === 0) {
          throw new Error('Canvas is empty — element may not have rendered correctly.');
        }

        // A4 in mm, 10 mm margins on each side
        const PDF_W = 210;
        const PDF_H = 297;
        const MARGIN = 10;
        const contentW = PDF_W - MARGIN * 2;  // 190 mm printable width
        const contentH = PDF_H - MARGIN * 2;  // 277 mm printable height

        // How many mm tall is the full image when scaled to fill contentW?
        const imgMmH = (imgPxH / imgPxW) * contentW;
        const totalPages = Math.ceil(imgMmH / contentH);

        const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

        for (let page = 0; page < totalPages; page++) {
          if (page > 0) pdf.addPage();

          const srcYMm  = page * contentH;
          const srcYPx  = (srcYMm  / imgMmH) * imgPxH;
          const sliceHPx = Math.min((contentH / imgMmH) * imgPxH, imgPxH - srcYPx);

          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width  = imgPxW;
          sliceCanvas.height = Math.ceil(sliceHPx);
          const ctx = sliceCanvas.getContext('2d')!;
          ctx.drawImage(canvas, 0, srcYPx, imgPxW, sliceHPx, 0, 0, imgPxW, sliceHPx);

          const sliceHMm = (sliceHPx / imgPxH) * imgMmH;
          pdf.addImage(sliceCanvas.toDataURL('image/jpeg', 0.92), 'JPEG',
            MARGIN, MARGIN, contentW, sliceHMm);
        }

        const safeName = recordsName.replace(/[^a-zA-Z0-9_\-]/g, '_');
        pdf.save(`Alumni_Report_${safeStr(alumniId)}_${safeName}.pdf`);
      } catch (err) {
        console.error('[PDF] generation error:', err);
        const msg = err instanceof Error ? err.message : String(err);
        alert(`PDF generation failed: ${msg}`);
      } finally {
        if (!cancelled) setIsPrinting(false);
      }
    };

    generate();
    return () => { cancelled = true; };
  }, [isPrinting, pdfRootRef.current]); // eslint-disable-line react-hooks/exhaustive-deps



  const handleScrapeLinkedIn = async () => {
    if (!id) return;
    setIsScraping(true);
    setScrapeSuccess(false);
    try {
      await linkedinService.scrapeByDonorId(id);
      const refreshed = await alumniService.getAlumniById(id);
      setAlumni(refreshed);
      setScrapeSuccess(true);
      setTimeout(() => setScrapeSuccess(false), 4000);
    } catch (err: unknown) {
      alert(`LinkedIn scrape failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsScraping(false);
    }
  };

  const handleUpdateLinkedin = async () => {
    if (!id || !editLinkedinValue.trim()) return;
    setIsSavingLinkedin(true);
    try {
      const updated = await alumniService.updateLinkedinPath(id, editLinkedinValue);
      setAlumni(updated);
      setIsEditingLinkedin(false);
    } catch (err: unknown) {
      alert(`Failed to update LinkedIn path: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsSavingLinkedin(false);
    }
  };

  const handleClearLinkedinFlag = async (revertToManual: boolean) => {
    if (!id) return;
    setIsClearingFlag(true);
    try {
      const updated = await alumniService.clearLinkedinFlag(id, revertToManual);
      setAlumni(updated);
    } catch (err: unknown) {
      alert(`Failed to clear flag: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsClearingFlag(false);
    }
  };

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500 gap-3 font-medium">
        <Spinner size={28} className="animate-spin text-accent" />
        Loading profile...
      </div>
    );
  }

  if (error || !alumni) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-600 p-6 rounded-2xl max-w-lg">
        <h3 className="font-bold text-lg flex items-center gap-2"><Warning size={20} />Error loading profile</h3>
        <p className="mt-2 text-sm">{error || 'Profile not found'}</p>
        <Button onClick={goBack} className="mt-4" variant="outline">Back</Button>
      </div>
    );
  }

  // ─── Data extraction ───────────────────────────────────────────────────────
  const sInfo = alumni.source_info;
  const lProfile = alumni.linkedin_profile as any;
  const lInfo = alumni.linkedin_info;
  const aiData = alumni.ai_research?.structured_data;
  const aiMeta = alumni.ai_research;

  const alumniId = sInfo?.alumni_id || sInfo?.alumni_csr_id || alumni.donor_id;
  const recordsName = sInfo?.csv_name || sInfo?.current_name || lInfo?.name || lProfile?.name || 'Alumni';

  const hasResearch = !!(
    aiData?.brief ||
    aiData?.targeted_donation_amount ||
    aiData?.potential_donor_score ||
    aiData?.wealth_signals ||
    aiData?.philanthropic_interests ||
    aiData?.board_and_advisory ||
    aiData?.media_and_profiles ||
    aiData?.awards_and_recognition ||
    aiData?.approach_for_cause ||
    aiData?.interests ||
    aiData?.career_trajectory ||
    aiData?.estimated_donation_capacity
  );

  // LinkedIn timeline
  const expList = (lProfile?.experience ?? lInfo?.experience ?? []) as Record<string, unknown>[];
  const eduList = (lProfile?.education ?? lInfo?.education ?? []) as Record<string, unknown>[];

  // Additional LinkedIn fields
  const linkedinUrl = lInfo?.url || lProfile?.url || sInfo?.linked_in || '';
  // Adjust these field names if your scraper stores the photo under a different key
  const photoUrl = lInfo?.photo || lInfo?.profile_picture || lInfo?.photo_url || lProfile?.profilePicture || lProfile?.photo || '';
  const location = lInfo?.location || lProfile?.location || sInfo?.location || '';
  const rawSalesforceUrl = sInfo?.salesforce_url || sInfo?.salesforce_link || '';
  const salesforceUrl = typeof rawSalesforceUrl === 'string' && rawSalesforceUrl.trim() !== ''
    ? (rawSalesforceUrl.trim().startsWith('http')
        ? rawSalesforceUrl.trim()
        : (rawSalesforceUrl.trim().startsWith('/lightning')
            ? `https://iitm-developmentoffice-india.lightning.force.com${rawSalesforceUrl.trim()}`
            : ''))
    : '';
  const aboutText = lInfo?.about || lProfile?.about || '';
  const skillsList = (lInfo?.skills ?? []) as Array<{ name?: string; endorsements?: string }>;
  const volunteerList = (lInfo?.volunteering ?? []) as Array<{ role?: string; duration?: string; startDate?: string; endDate?: string; organizationName?: string; organizationLinkedinUrl?: string; cause?: string }>;
  const languagesList = (lInfo?.languages ?? []) as Array<{ title?: string; subtitle?: string }>;
  const projectsList = (lInfo?.projects ?? []) as Array<{ title?: string; start_date?: string; end_date?: string; description?: string }>;
  const certList = (lInfo?.certifications ?? []) as Array<{ title?: string; subtitle?: string; meta?: string; credential_url?: string; credential_id?: string }>;
  const pubList = (lInfo?.publications ?? []) as Array<{ title?: string; description?: string; date?: string; publisher?: string; url?: string }>;
  const patentList = (lInfo?.patents ?? []) as Array<{ title?: string; date_issued?: string; patents_id?: string; description?: string }>;
  const honorsList = (lInfo?.honors_and_awards ?? lInfo?.honorsAndAwards ?? []) as Array<{ title?: string; publication?: string; date?: string; description?: string; issuedBy?: string; issuedAt?: string }>;
  const orgList = (lInfo?.organizations ?? []) as Array<{ title?: string; membership_type?: string; start_date?: string; end_date?: string }>;
  const coursesList = (lInfo?.courses ?? []) as Array<{ title?: string; subtitle?: string }>;

  const ct = aiData?.career_trajectory;

  const activityList = (lInfo?.activity ?? []) as Array<{ interaction?: string; link?: string; title?: string; img?: string; id?: string }>;
  const peopleViewedList = (lInfo?.people_also_viewed ?? []) as Array<{ profile_link?: string; name?: string; about?: string; location?: string }>;
  const mondayData = (alumni.monday_data ?? []) as any[];

  const fyDonations = [
    { year: 'FY21', amount: parseMoney(sInfo?.amount_donated_in_the_fy21) },
    { year: 'FY22', amount: parseMoney(sInfo?.amount_donated_in_the_fy22) },
    { year: 'FY23', amount: parseMoney(sInfo?.amount_donated_in_the_fy23) },
    { year: 'FY24', amount: parseMoney(sInfo?.amount_donated_in_the_fy24) },
    { year: 'FY25', amount: parseMoney(sInfo?.amount_donated_in_the_fy25) },
    { year: 'FY26', amount: parseMoney(sInfo?.amount_donated_in_the_fy26) },
  ];
  const hasFYDonations = fyDonations.some(d => d.amount > 0);
  const estimatedFinancialCapacity = (alumni.vals_framework as any)?.estimated_financial_capacity;
  const estimatedNetWorth = formatCapacityValue(estimatedFinancialCapacity?.net_worth_inr);
  const recommendedAsk = formatCapacityValue(
    estimatedFinancialCapacity?.recommended_ask_inr || aiData?.targeted_donation_amount
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <User size={18} /> },
    { id: 'giving', label: 'Giving & CRM', icon: <Bank size={18} /> },
    { id: 'intelligence', label: 'Intelligence & VALS', icon: <Robot size={18} /> },
    { id: 'network', label: 'Similar Donors', icon: <UsersThree size={18} /> }
  ];

  const peopleAlsoViewedSection = (
    <>
      {peopleViewedList.length > 0 && (
        <SectionCard>
          <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
            <UsersThree size={22} className="text-slate-700" weight="duotone" />
            PEOPLE ALSO VIEWED
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {peopleViewedList.map((p, i) => (
              <a key={i} href={p.profile_link} target="_blank" rel="noopener noreferrer" className="flex flex-col gap-2 p-4 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors">
                <h4 className="font-bold text-slate-800 text-sm line-clamp-1">{p.name}</h4>
                <p className="text-xs font-semibold text-slate-500">{p.location}</p>
                {p.about && <p className="text-xs text-slate-600 line-clamp-2 mt-1">{p.about}</p>}
              </a>
            ))}
          </div>
        </SectionCard>
      )}
    </>
  );

  const similarDonorsSection = (
    <>
      {(similarAlumni.length > 0 || loadingSimilar) && (
        <SectionCard className="no-print">
          <div className="flex items-center justify-between border-b border-slate-100 pb-5 mb-5">
            <h3 className="font-outfit font-extrabold text-xl text-slate-800 flex items-center gap-2">
              <UsersThree size={22} className="text-accent" weight="duotone" />
              SIMILAR DONORS
            </h3>
            <span className="text-xs font-semibold text-slate-400">
              {loadingSimilar && similarAlumni.length === 0 ? (
                <span className="flex items-center gap-1.5">
                  <Spinner size={14} className="animate-spin text-accent" />
                  Finding similar donors...
                </span>
              ) : (
                `Found ${similarAlumni.length} match${similarAlumni.length !== 1 ? 'es' : ''}`
              )}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {similarAlumni.map((item) => {
              const compScore = item.composite_score ?? 0;
              const breakdown = item.similarity_breakdown;

              const itemAny = item as any;
              const isInfo = item.source_info;
              const estimatedCapacity =
                (item.vals_framework as any)?.estimated_financial_capacity ||
                itemAny.estimated_financial_capacity ||
                isInfo?.estimated_financial_capacity;
              const structuredData = item.ai_research?.structured_data as any;
              const recommendedAskValue = findValueByKeys(item, [
                'recommended_ask_inr',
                'recommended_ask',
                'recommendedAsk',
                'recommended_ask_amount',
                'suggested_ask',
                'ask_amount',
                'target_ask_inr',
                'target_ask',
                'targetAsk',
                'targeted_donation_amount_inr',
                'targeted_donation_amount',
              ]);
              const givingPropensityValue = findValueByKeys(item, [
                'donation_propensity',
                'giving_propensity',
                'givingPropensity',
                'donor_propensity',
                'donorPropensity',
                'propensity',
              ]);
              const cumulativeGiving = formatToCr(safeStr(isInfo?.cumulative_giving_to_iitm || '0'));
              const recommendedAsk = formatCapacityValue(
                estimatedCapacity?.recommended_ask_inr ??
                recommendedAskValue ??
                itemAny.recommended_ask_inr ??
                itemAny.recommended_ask ??
                itemAny.recommendedAsk ??
                itemAny.recommended_ask_amount ??
                itemAny.suggested_ask ??
                itemAny.ask_amount ??
                itemAny.target_ask_inr ??
                itemAny.target_ask ??
                itemAny.targetAsk ??
                itemAny.targeted_donation_amount_inr ??
                itemAny.targeted_donation_amount ??
                isInfo?.recommended_ask_inr ??
                isInfo?.recommended_ask ??
                isInfo?.recommendedAsk ??
                isInfo?.recommended_ask_amount ??
                isInfo?.target_ask_inr ??
                isInfo?.target_ask ??
                structuredData?.recommended_ask_inr ??
                structuredData?.recommended_ask ??
                structuredData?.targeted_donation_amount
              );
              const givingPropensity = safeStr(
                (item.vals_framework as any)?.donation_propensity ??
                givingPropensityValue ??
                itemAny.donation_propensity ??
                itemAny.giving_propensity ??
                structuredData?.donation_propensity,
                'Not Available'
              );
              const title = isInfo?.designation || '';
              const company = isInfo?.company || '';
              const dept = isInfo?.department || '';
              const degree = isInfo?.degree || '';
              const batch = isInfo?.batch || '';

              return (
                <div
                  key={item._id}
                  onClick={() => {
                    navigate(`/alumni/${item.donor_id || item._id}`);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 hover:border-accent/50 hover:shadow-[0_12px_28px_-10px_rgba(249,115,22,0.25)] hover:-translate-y-1 cursor-pointer transition-all duration-300 flex flex-col justify-between group"
                >
                  <div>
                    <div className="flex justify-between items-start gap-2 mb-3">
                      <h4 className="font-outfit font-extrabold text-base text-slate-800 group-hover:text-accent transition-colors line-clamp-1">
                        {item.name || 'Unknown Alumni'}
                      </h4>
                      <span className="shrink-0 text-[11px] font-mono font-extrabold px-2.5 py-1 rounded-full bg-accent text-white shadow-sm">
                        {compScore}% Match
                      </span>
                    </div>

                    <div className="space-y-1 mb-4 text-xs font-semibold text-slate-500">
                      {(title || company) && (
                        <p className="line-clamp-1">
                          {title} {title && company && 'at'} {company}
                        </p>
                      )}
                      {(degree || dept || batch) && (
                        <p className="text-slate-400 text-[11px]">
                          {degree} {dept && `(${dept})`} {batch && `- Batch of ${batch}`}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                      <div className="rounded-xl border border-slate-200 bg-white p-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                              Cumulative Giving
                        </p>
                        <p className="mt-1 text-sm font-extrabold text-emerald-700">
                          {cumulativeGiving}
                        </p>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Recommended Ask
                      </p>
                      <p className="mt-1 text-sm font-extrabold text-accent">
                          {recommendedAsk}
                      </p>
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Giving Propensity
                        </p>
                        <p className="mt-1 text-sm font-extrabold text-slate-800">
                          {givingPropensity}
                        </p>
                      </div>
                    </div>
                  </div>

                  {breakdown && (
                    <div className="border-t border-slate-200/60 pt-3 mt-auto space-y-2">
                      <div className="flex items-center justify-between text-[10px] font-bold text-slate-500">
                        <span>VALS FIT (8)</span>
                        <span className="font-mono text-slate-700">{breakdown.vals}%</span>
                      </div>
                      <div className="w-full bg-slate-200/60 rounded-full h-1 overflow-hidden">
                        <div className="bg-accent h-full rounded-full transition-all duration-500" style={{ width: `${breakdown.vals}%` }} />
                      </div>

                      <div className="flex items-center justify-between text-[10px] font-bold text-slate-500">
                        <span>INTERESTS</span>
                        <span className="font-mono text-slate-700">{breakdown.interest}%</span>
                      </div>
                      <div className="w-full bg-slate-200/60 rounded-full h-1 overflow-hidden">
                        <div className="bg-accent h-full rounded-full transition-all duration-500" style={{ width: `${breakdown.interest}%` }} />
                      </div>

                      <div className="flex items-center justify-between text-[10px] font-bold text-slate-500">
                        <span>NET WORTH</span>
                        <span className="font-mono text-slate-700">{breakdown.wealth}%</span>
                      </div>
                      <div className="w-full bg-slate-200/60 rounded-full h-1 overflow-hidden">
                        <div className="bg-accent h-full rounded-full transition-all duration-500" style={{ width: `${breakdown.wealth}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {loadingSimilar && [1, 2, 3].map((n) => (
              <SimilarDonorSkeleton key={`skeleton-${n}`} />
            ))}
          </div>

          {((similarAlumni.length === similarLimit && similarLimit < 20) || (loadingSimilar && similarAlumni.length > 0)) && (
            <div className="flex justify-center mt-6">
              <Button
                onClick={() => setSimilarLimit(prev => Math.min(20, prev + 5))}
                disabled={loadingSimilar}
                className="flex items-center gap-1.5 text-xs px-5 py-2 hover:bg-slate-50"
                variant="outline"
              >
                {loadingSimilar ? (
                  <>
                    <Spinner size={14} className="animate-spin" />
                    Loading More...
                  </>
                ) : (
                  <>Show More Similar Donors</>
                )}
              </Button>
            </div>
          )}
        </SectionCard>
      )}
    </>
  );

  return (
    <>
    <div className="space-y-6 font-sans max-w-7xl mx-auto pb-16">

      {/* ── Breadcrumb ── */}
      <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold uppercase tracking-wider no-print">
        <button onClick={() => navigate('/')} className="hover:text-accent transition-colors">Dashboard</button>
        <span>/</span>
        {(routerLocation.state as any)?.from === 'constellation' ? (
          <button onClick={() => navigate('/')} className="hover:text-accent transition-colors">Constellation</button>
        ) : (
          <button onClick={() => navigate('/alumni')} className="hover:text-accent transition-colors">Alumni Directory</button>
        )}
        <span>/</span>
        <span className="text-slate-700">Profile</span>
      </div>

      {/* ── Hero Profile Card ── */}
      <ProfileHeroCard
        name={recordsName}
        alumniId={safeStr(alumniId)}
        designation={sInfo?.designation}
        company={sInfo?.company}
        location={location}
        photoUrl={photoUrl}
        skills={skillsList}
        linkedinUrl={linkedinUrl}
        salesforceUrl={salesforceUrl}
        onBack={goBack}
      />

      {/* ── Tabs + Scrape LinkedIn row ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap no-print">
        {/* Tab pills */}
        <div className="inline-flex items-center gap-1 bg-slate-100/80 border border-slate-200/60 rounded-2xl p-1.5 overflow-x-auto no-scrollbar">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-200 ${
                activeTab === t.id
                  ? 'bg-white text-slate-900 shadow-[0_2px_10px_rgba(15,23,42,0.12)]'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Right-side actions: Download PDF + Scrape LinkedIn */}
        <div className="flex items-start gap-2 shrink-0">

          {/* Download PDF button */}
          <Button
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={isPrinting}
            title="Download profile as PDF"
            className="flex items-center gap-1.5 text-xs justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPrinting ? (
              <Spinner size={14} className="animate-spin text-accent" />
            ) : (
              <FilePdf size={14} className="text-rose-500" weight="duotone" />
            )}
            <span>{isPrinting ? 'Preparing…' : 'Download PDF'}</span>
          </Button>

          {/* Manual LinkedIn Entry */}
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-1">
              {isEditingLinkedin ? (
                <>
                  <input
                    type="text"
                    value={editLinkedinValue}
                    onChange={(e) => setEditLinkedinValue(e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    className="text-xs px-2 py-1.5 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-accent w-48"
                  />
                  <Button
                    variant="primary"
                    onClick={handleUpdateLinkedin}
                    disabled={isSavingLinkedin}
                    className="text-[10px] px-2 py-1.5 h-auto min-h-0"
                  >
                    {isSavingLinkedin ? <Spinner size={12} className="animate-spin" /> : 'Save'}
                  </Button>
                  <button onClick={() => setIsEditingLinkedin(false)} className="text-[10px] px-2 py-1 text-slate-500 hover:text-slate-700">Cancel</button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setEditLinkedinValue(linkedinUrl || '');
                      setIsEditingLinkedin(true);
                    }}
                    className="text-xs text-slate-500 hover:text-accent font-medium px-2 py-1 flex items-center gap-1"
                    title="Manually enter or correct LinkedIn URL"
                  >
                    <PencilSimple size={12} /> Edit LinkedIn URL
                  </button>
                </>
              )}
            </div>
            {alumni?.manual_linkedin && (
              <span className="text-[9px] text-slate-400 max-w-[200px] text-right leading-tight">
                Last set by {alumni.manual_linkedin.entered_by} on {new Date(alumni.manual_linkedin.entered_at).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Scrape LinkedIn section */}
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="outline"
              onClick={handleScrapeLinkedIn}
              disabled={isScraping || !linkedinUrl}
              title={!linkedinUrl ? 'No LinkedIn URL on file' : 'Re-scrape LinkedIn profile data'}
              className="flex items-center gap-1.5 text-xs w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isScraping ? (
                <Spinner size={14} className="animate-spin text-accent" />
              ) : (
                <svg className="w-3.5 h-3.5 fill-current text-[#0A66C2]" viewBox="0 0 24 24" aria-hidden>
                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                </svg>
              )}
              <span>{isScraping ? 'Scraping…' : 'Scrape LinkedIn'}</span>
              {/* Green status dot */}
              {!isScraping && scrapeSuccess && (
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" title="Scraped successfully" />
              )}
              {!isScraping && !scrapeSuccess && !!(alumni?.linkedin_info as Record<string,unknown>)?.name && (
                <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" title="LinkedIn data on file" />
              )}
            </Button>
            {alumni?.linkedin_scraped_at && (
              <span className="text-[10px] text-slate-400 font-medium px-1">
                Last scraped: {new Date(alumni.linkedin_scraped_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* ── LinkedIn Overwrite Banner ── */}
      {alumni?.linkedin_overwrite_flag && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-amber-50 border border-amber-200 mt-4 text-sm animate-in fade-in">
          <div className="flex gap-3 text-amber-800">
            <Warning size={20} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">LinkedIn path was overwritten by import</p>
              <p className="mt-1 text-amber-700/90 text-xs">
                Previously manually set to <a href={alumni.manual_linkedin?.url} target="_blank" rel="noopener noreferrer" className="font-semibold underline hover:text-amber-900">{alumni.manual_linkedin?.url}</a> by {alumni.manual_linkedin?.entered_by} on {alumni.manual_linkedin?.entered_at && new Date(alumni.manual_linkedin.entered_at).toLocaleDateString()}.
              </p>
            </div>
          </div>
          {localStorage.getItem('role') === 'admin' && (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={() => handleClearLinkedinFlag(false)}
                disabled={isClearingFlag}
                className="bg-white hover:bg-amber-100 text-amber-700 border-amber-300 text-xs px-3 py-1.5 min-h-[32px] h-auto rounded-lg"
              >
                Dismiss Flag
              </Button>
              <Button
                variant="primary"
                onClick={() => handleClearLinkedinFlag(true)}
                disabled={isClearingFlag}
                className="bg-amber-600 hover:bg-amber-700 text-white border-transparent text-xs px-3 py-1.5 min-h-[32px] h-auto rounded-lg"
              >
                Revert to Manual
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── TAB CONTENT: OVERVIEW ── */}
      {activeTab === 'overview' && (
        <div className="space-y-6 animate-in fade-in duration-300 pt-1">

      {/* ── Section 1: Profile Chips & Raw Data ── */}
      <div className="relative print-page-1 mb-8">
        <GlowBackdrop className="w-72 h-56 -top-14 left-0" />
        <details className="group">
          <summary className="list-none cursor-pointer focus:outline-none flex items-center w-full">
            <div className="overflow-x-auto relative flex-1 cursor-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex gap-3 pb-1 min-w-max">
                <InfoChip label="Alumni ID" value={safeStr(alumniId)} />
                <InfoChip label="IITM Records Name" value={safeStr(recordsName)} />
                <InfoChip label="Batch" value={safeStr(sInfo?.batch)} />
                <InfoChip label="Degree" value={safeStr(sInfo?.degree)} />
                <InfoChip label="Department" value={safeStr(sInfo?.department)} />
                <InfoChip label="Company" value={safeStr(sInfo?.company)} />
                <InfoChip label="Designation" value={safeStr(sInfo?.designation)} />
              </div>
            </div>
            
            <div 
              className="shrink-0 ml-4 p-2 bg-slate-50 border border-slate-200 hover:bg-slate-100 rounded-full transition-colors flex items-center justify-center text-slate-500 no-print" 
              title="View all raw data"
            >
              <svg className="w-4 h-4 fill-current group-open:rotate-180 transition-transform duration-200" viewBox="0 0 256 256">
                <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
              </svg>
            </div>
          </summary>
          
          <div className="flex flex-col gap-6 mt-6 no-print bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            {sInfo && Object.keys(sInfo).filter(k => !SOURCE_INFO_EXCLUDE.has(k) && sInfo[k] != null && sInfo[k] !== '').length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-slate-500 flex items-center gap-2 mb-4 uppercase tracking-widest border-b border-slate-100 pb-2">
                  <Database size={16} weight="duotone" />
                  Raw Source Data
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(sInfo)
                    .filter(([k, v]) => !SOURCE_INFO_EXCLUDE.has(k) && v != null && v !== '')
                    .map(([k, v]) => (
                      <div key={k} className="flex flex-col gap-1 bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{k.replace(/_/g, ' ')}</span>
                        <span className="text-sm font-medium text-slate-800 break-words">{safeStr(v)}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {lInfo && Object.keys(lInfo).filter(k => !LINKEDIN_INFO_EXCLUDE.has(k) && (lInfo as any)[k] != null && (lInfo as any)[k] !== '').length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-slate-500 flex items-center gap-2 mb-4 uppercase tracking-widest border-b border-slate-100 pb-2">
                  <Database size={16} weight="duotone" />
                  Raw LinkedIn Data
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(lInfo)
                    .filter(([k, v]) => !LINKEDIN_INFO_EXCLUDE.has(k) && v != null && v !== '')
                    .map(([k, v]) => (
                      <div key={k} className="flex flex-col gap-1 bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{k.replace(/_/g, ' ')}</span>
                        <span className="text-sm font-medium text-slate-800 break-words">{safeStr(v)}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </details>
      </div>

      {/* ── About Section ── */}
      {aboutText && (
        <SectionCard>
          <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-3 flex items-center gap-2">
            <User size={22} className="text-slate-700" weight="duotone" />
            ABOUT
          </h3>
          <p className="text-slate-600 text-sm font-medium leading-relaxed whitespace-pre-wrap">
            {aboutText}
          </p>
        </SectionCard>
      )}

      {/* ── Section 2: Experience & Education (Side-by-side) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print-page-2">

        {/* Experience */}
        <SectionCard>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-outfit font-extrabold text-xl text-slate-800 flex items-center gap-2">
              <Briefcase size={22} className="text-slate-700" weight="duotone" />
              EXPERIENCE
              {expList.length > 0 && (
                <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-sky-100 text-sky-700 ml-1.5">
                  {expList.length}
                </span>
              )}
            </h3>
          </div>
          {expList.length > 0 ? (
            <div className="space-y-0">
              {expList.map((item, i) => (
                <TimelineItem key={i} item={item} type="experience" />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <p className="font-medium text-sm">No experience data available</p>
            </div>
          )}
        </SectionCard>

        {/* Education */}
        <SectionCard>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-outfit font-extrabold text-xl text-slate-800 flex items-center gap-2">
              <GraduationCap size={22} className="text-accent" weight="duotone" />
              EDUCATION
              {eduList.length > 0 && (
                <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 ml-1.5">
                  {eduList.length}
                </span>
              )}
            </h3>
          </div>
          {eduList.length > 0 ? (
            <div className="space-y-0">
              {eduList.map((item, i) => (
                <TimelineItem key={i} item={item} type="education" />
              ))}
            </div>
          ) : (
            <div className="space-y-0">
              {/* Fallback to source_info */}
              {sInfo?.degree || sInfo?.department ? (
                <div className="flex gap-3 py-3">
                  <div className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center text-[11px] font-black text-white bg-gradient-to-br from-accent to-accent-hover shadow-sm">
                    IIT
                  </div>
                  <div>
                    <p className="font-bold text-slate-800 text-sm">IIT Madras</p>
                    <p className="text-slate-500 font-medium text-xs mt-0.5">{safeStr(sInfo?.degree)} · {safeStr(sInfo?.department)}</p>
                    <p className="text-slate-400 text-[11px] mt-0.5">Batch {safeStr(sInfo?.batch)}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <p className="font-medium text-sm">No education data available</p>
                </div>
              )}
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── Section 2.5: Professional Credentials & Background ── */}
      {(skillsList.length > 0 || volunteerList.length > 0 || languagesList.length > 0 || projectsList.length > 0 || certList.length > 0 || pubList.length > 0 || patentList.length > 0 || honorsList.length > 0 || orgList.length > 0 || coursesList.length > 0) && (
        <div className="space-y-6 pt-2">
          <h2 className="font-outfit font-black text-2xl text-slate-900 tracking-tight">LinkedIn Professional Details</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {skillsList.length > 0 && <SkillsSection skills={skillsList} />}
            {honorsList.length > 0 && <HonorsSection honors={honorsList} />}
            {certList.length > 0 && <CertificationsSection certifications={certList} />}
            {projectsList.length > 0 && <ProjectsSection projects={projectsList} />}
            {volunteerList.length > 0 && <VolunteeringSection volunteering={volunteerList} />}
            {pubList.length > 0 && <PublicationsSection publications={pubList} />}
            {patentList.length > 0 && <PatentsSection patents={patentList} />}
            {orgList.length > 0 && <OrganizationsSection organizations={orgList} />}
            {coursesList.length > 0 && <CoursesSection courses={coursesList} />}
            {languagesList.length > 0 && <LanguagesSection languages={languagesList} />}
          </div>
        </div>
      )}

      {activityList.length > 0 && (
        <SectionCard>
          <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
            <ThumbsUp size={22} className="text-slate-700" weight="duotone" />
            RECENT ACTIVITY
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activityList.map((act, i) => (
              <a key={i} href={act.link} target="_blank" rel="noopener noreferrer" className="flex flex-col border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors">
                <span className="text-xs font-bold text-slate-400 mb-2">{act.interaction}</span>
                {act.img && (
                  <div className="w-full h-32 mb-3 bg-slate-100 rounded-lg overflow-hidden border border-slate-200/50">
                    <img src={act.img} alt="Activity" className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="text-sm font-semibold text-slate-700 line-clamp-3">{act.title}</p>
              </a>
            ))}
          </div>
        </SectionCard>
      )}

      {peopleAlsoViewedSection}

        </div>
      )}

      {/* ── TAB CONTENT: GIVING & CRM ── */}
      {activeTab === 'giving' && (
        <div className="space-y-6 animate-in fade-in duration-300 pt-1">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SectionCard className="md:col-span-2 relative overflow-hidden" elevated>
              <GlowBackdrop className="w-64 h-64 -top-20 -right-10" />
              <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2 relative">
                <Bank size={22} className="text-slate-700" weight="duotone" />
                DONATION SUMMARY
              </h3>
              <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <HeroStat
                  label="Cumulative Giving"
                  value={formatProfileCurrency(sInfo?.cumulative_giving_to_iitm, '₹0')}
                  sublabel="Lifetime contribution to IITM"
                  icon={<Coins size={22} weight="duotone" />}
                />
                <div className="sm:col-span-2 grid grid-cols-2 gap-4">
                  <InfoChip label="Largest Gift" value={formatProfileCurrency(sInfo?.largest_gift_to_iitm)} />
                  <InfoChip label="Smallest Gift" value={formatProfileCurrency(sInfo?.smallest_gift)} />
                  <InfoChip label="Net Contribution" value={formatProfileCurrency(sInfo?.net_contribution)} />
                  <InfoChip label="Total Payments" value={formatProfileNumber(sInfo?.total_number_of_payments)} />
                </div>
              </div>
              <div className="relative grid grid-cols-2 gap-4">
                <InfoChip label="First Gift Date" value={safeStr(sInfo?.date_of_first_gift_to_iitm)} />
                <InfoChip label="Last Gift Date" value={safeStr(sInfo?.date_of_last_gift_to_iitm)} />
              </div>
            </SectionCard>
            <SectionCard>
              <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
                <Info size={22} className="text-slate-700" weight="duotone" />
                CRM STATUS
              </h3>
              <div className="flex flex-col gap-3">
                {sInfo?.hni && <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-xl font-bold text-sm text-center">High Net Worth Individual (HNI)</div>}
                {sInfo?.daa && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-xl font-bold text-sm text-center">Distinguished Alumnus (DAA)</div>}
                {sInfo?.do_not_solicit && <div className="bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 rounded-xl font-bold text-sm text-center">Do Not Solicit</div>}
                {sInfo?.potential_donor && <div className="bg-sky-50 border border-sky-200 text-sky-800 px-3 py-2 rounded-xl font-bold text-sm text-center">Potential Donor</div>}
                <div className="text-xs font-semibold text-slate-500 mt-2">Sales Person: <span className="text-slate-800">{safeStr(sInfo?.sales_person)}</span></div>
                <div className="text-xs font-semibold text-slate-500">Last Touch: <span className="text-slate-800">{safeStr(sInfo?.last_touch)}</span></div>
                <div className="text-xs font-semibold text-slate-500">Record Type: <span className="text-slate-800">{safeStr(sInfo?.account_record_type)}</span></div>
              </div>
            </SectionCard>
          </div>

          {hasFYDonations && (
            <SectionCard>
              <h3 className="font-outfit font-extrabold text-xl text-slate-800 mb-5 flex items-center gap-2">
                <ChartBar size={22} className="text-slate-700" weight="duotone" />
                FINANCIAL YEAR GIVING
              </h3>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={fyDonations} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="fyGivingFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="year" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis tickFormatter={(v) => formatIndianCurrency(Number(v), '₹0')} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                    <RechartsTooltip
                      cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '4 4' }}
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl p-3 shadow-lg">
                              <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>
                              <p className="text-sm font-bold text-slate-800">
                                {formatIndianCurrency(Number(payload[0].value), '₹0')}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#ea580c"
                      strokeWidth={3}
                      fill="url(#fyGivingFill)"
                      dot={{ r: 4, fill: '#ea580c', strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6, fill: '#ea580c', strokeWidth: 3, stroke: '#fff' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>
          )}

          {mondayData.length > 0 && (() => {
            const grouped: Record<string, { total: number, payments: any[] }> = {};
            mondayData.forEach((donation: any) => {
              const stage = donation.stage || 'Unknown Stage';
              if (!grouped[stage]) {
                grouped[stage] = { total: 0, payments: [] };
              }
              grouped[stage].payments.push(donation);
              grouped[stage].total += (Number(donation.amount) || 0);
            });
            const sortedGroups = Object.entries(grouped).sort((a, b) => b[1].total - a[1].total);

            return (
              <div className="space-y-6">
                {sortedGroups.map(([stage, data], groupIdx) => (
                  <SectionCard key={groupIdx} className="!p-0 overflow-hidden">
                    <div className="flex justify-between items-center p-6 pb-4 bg-slate-50 border-b border-slate-200">
                      <h3 className="font-outfit font-extrabold text-xl text-slate-800 flex items-center gap-2">
                        <CalendarBlank size={22} className="text-slate-700" weight="duotone" />
                        {stage.toUpperCase()} TRANSACTIONS
                      </h3>
                      <span className="font-extrabold text-slate-900">
                        Total: INR {formatIndianNumber(data.total, '0')}
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50/80 border-y border-slate-200/70">
                            <th className="py-3 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Date</th>
                            <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Closure Date</th>
                            <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Amount</th>
                            <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Project</th>
                            <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Type / Category</th>
                            <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Probability</th>
                            <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Through</th>
                            <th className="py-3 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.payments.map((tx, idx) => (
                            <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-orange-50/40 transition-colors">
                              <td className="py-3.5 px-6 text-sm text-slate-600 whitespace-nowrap">
                                {tx.date?.$date ? new Date(tx.date.$date).toLocaleDateString() : (typeof tx.date === 'string' && tx.date.includes('T') ? new Date(tx.date).toLocaleDateString() : (typeof tx.date === 'string' ? tx.date : 'N/A'))}
                              </td>
                              <td className="py-3.5 px-4 text-sm text-slate-600 whitespace-nowrap">
                                {tx.closure_date?.$date ? new Date(tx.closure_date.$date).toLocaleDateString() : (typeof tx.closure_date === 'string' && tx.closure_date.includes('T') ? new Date(tx.closure_date).toLocaleDateString() : (typeof tx.closure_date === 'string' ? tx.closure_date : 'N/A'))}
                              </td>
                              <td className="py-3.5 px-4 text-sm font-semibold text-slate-800 whitespace-nowrap">
                                {tx.currency || 'INR'} {formatIndianNumber(tx.amount, '0')}
                              </td>
                              <td className="py-3.5 px-4 text-sm text-slate-700">{tx.project || 'N/A'}</td>
                              <td className="py-3.5 px-4 text-sm text-slate-600">
                                <div>{tx.type || 'N/A'}</div>
                                {(tx.donor_category || tx.receipt_category) && (
                                  <div className="text-[10px] text-slate-400 mt-0.5">
                                    {[tx.donor_category, tx.receipt_category].filter(Boolean).join(' • ')}
                                  </div>
                                )}
                              </td>
                              <td className="py-3.5 px-4 text-sm text-slate-600">
                                {tx.probability !== undefined && tx.probability !== null ? `${tx.probability}%` : 'N/A'}
                              </td>
                              <td className="py-3.5 px-4 text-sm text-slate-600">{tx.donation_through || 'N/A'}</td>
                              <td className="py-3.5 px-6">
                                {tx.paid ? (
                                  <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full text-[10px] font-bold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> PAID
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-full text-[10px] font-bold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> PENDING
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </SectionCard>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── TAB CONTENT: INTELLIGENCE ── */}
      {activeTab === 'intelligence' && (
        <div className="space-y-6 animate-in fade-in duration-300 pt-1">

          {/* ── AI Research Trigger (if not yet researched) ── */}
          {!hasResearch && (
            <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-black rounded-3xl p-8 text-white border border-slate-800 shadow-[0_24px_48px_-16px_rgba(15,23,42,0.45)]">
              <div aria-hidden className="absolute -left-10 -top-10 w-56 h-56 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
              <div aria-hidden className="absolute -right-16 -bottom-10 w-64 h-64 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row items-center gap-6">
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-outfit font-extrabold text-2xl">AI Donor Intelligence</h3>
                  <p className="text-slate-400 font-medium text-sm mt-1.5 leading-relaxed max-w-xl">
                    Trigger a comprehensive web research process to gather philanthropic indicators, career trajectory analysis, wealth estimations, and donor intelligence.
                  </p>
                  {isResearching && (
                    <div className="mt-4 space-y-2">
                      <p className="text-accent text-xs font-bold animate-pulse">
                        {researchProgress || 'This may take 30–90 seconds. Please wait...'}
                      </p>
                      <FakeProgressBar estimatedTimeMs={180000} isComplete={!isResearching} className="w-full max-w-sm" />
                    </div>
                  )}
                </div>
                <button
                  onClick={handleResearch}
                  disabled={isResearching || alumni?.research_locked}
                  title={alumni?.research_locked ? 'Research is locked by admin' : ''}
                  className="shrink-0 bg-gradient-to-br from-accent to-accent-hover hover:brightness-110 disabled:from-slate-700 disabled:to-slate-700 text-white font-bold py-3.5 px-8 rounded-xl transition-all duration-300 shadow-lg shadow-accent/40 flex items-center gap-2.5 text-sm"
                >
                  {isResearching ? (
                    <><Spinner size={18} className="animate-spin" /> Researching...</>
                  ) : (
                    <span>{alumni?.research_locked ? '🔒 Locked' : 'Start AI Research'}</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ── AI Research Results ── */}
          {hasResearch && (
            <div className="space-y-6">

              {/* Status bar */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 no-print bg-white p-4 rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-xl w-max">
                    <CheckCircle size={18} weight="fill" />
                    <span className="font-bold text-sm">AI Research Completed</span>
                    {aiData?.research_quality && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ml-1 ${badgeColor(aiData.research_quality)}`}>
                        {aiData.research_quality} Quality
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-500">
                    {aiMeta?.search_mode && (
                      <span className="bg-slate-100 px-2 py-1 rounded-md border border-slate-200">
                        Mode: <span className="text-slate-700 capitalize">{aiMeta.search_mode}</span>
                      </span>
                    )}
                    {aiMeta?.researched_at && (
                      <span className="bg-slate-100 px-2 py-1 rounded-md border border-slate-200">
                        Researched: <span className="text-slate-700">{new Date(aiMeta.researched_at).toLocaleString()}</span>
                      </span>
                    )}
                    {aiMeta?.researched_by && (
                      <span className="bg-slate-100 px-2 py-1 rounded-md border border-slate-200">
                        By: <span className="text-slate-700">{aiMeta.researched_by}</span>
                      </span>
                    )}
                    <button
                      onClick={() => setHistoryOpen(true)}
                      className="no-print px-2 py-1 rounded-md border border-slate-200 text-accent hover:bg-accent/5 transition-colors"
                    >
                      Research history
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 self-start md:self-center">
                  {isAdmin && alumni && (
                    <button
                      onClick={async () => {
                        if (!id) return;
                        const newVal = !alumni.research_locked;
                        await alumniService.toggleResearchLock(id, newVal);
                        setAlumni({ ...alumni, research_locked: newVal });
                      }}
                      className="text-xs font-bold transition-colors flex items-center gap-1.5 border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-2 rounded-lg text-slate-500 hover:text-slate-700"
                      title={alumni.research_locked ? 'Unlock re-research' : 'Lock re-research'}
                    >
                      {alumni.research_locked ? <LockOpen size={14} weight="bold" /> : <Lock size={14} weight="bold" />}
                      {alumni.research_locked ? 'Unlock Research' : 'Lock Research'}
                    </button>
                  )}
                  <button
                    onClick={handleResearch}
                    disabled={isResearching || alumni?.research_locked}
                    title={alumni?.research_locked ? 'Research is locked by admin' : ''}
                    className="text-xs text-slate-500 hover:text-accent font-bold transition-colors flex items-center gap-1.5 border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResearching ? (
                      <Spinner size={14} className="animate-spin" />
                    ) : alumni?.research_locked ? (
                      <Lock size={14} weight="bold" />
                    ) : (
                      <ArrowClockwise size={14} weight="bold" />
                    )}
                    {alumni?.research_locked ? 'Locked' : 'Re-research'}
                  </button>
                </div>
              </div>

              {/* ── Section 1: Hero Verdict Row ── */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {aiData?.potential_donor_score && (
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-5 flex items-center gap-4 border border-slate-700">
                    <div aria-hidden className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-accent/25 blur-2xl pointer-events-none" />
                    <div className="relative w-14 h-14 shrink-0">
                      <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#334155" strokeWidth="3.5" />
                        <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#fb923c" strokeWidth="3.5"
                          strokeDasharray={`${scoreWidth(aiData.potential_donor_score)} 100`} strokeLinecap="round" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center font-black text-white text-sm">
                        {scoreWidth(aiData.potential_donor_score)}
                      </span>
                    </div>
                    <div className="relative">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Donor Score</p>
                      <p className="font-black text-white text-xl leading-none">
                        {scoreWidth(aiData.potential_donor_score)}<span className="text-slate-500 text-sm font-bold">/100</span>
                      </p>
                      <p className="text-slate-500 text-[10px] font-medium mt-1">Composite AI rating</p>
                    </div>
                  </div>
                )}

                {aiData?.readiness_to_give && (
                  <div className="relative rounded-2xl bg-white border border-slate-200/70 p-5 flex flex-col justify-between shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Readiness to Give</p>
                    <div className="flex items-center gap-2.5">
                      <span className={`w-3 h-3 rounded-full shrink-0 ${badgeDotColor(aiData.readiness_to_give)}`} />
                      <span className="font-extrabold text-2xl text-slate-900">{aiData.readiness_to_give}</span>
                    </div>
                    <p className="text-slate-400 text-[10px] font-medium mt-2">Current giving propensity</p>
                  </div>
                )}

                {recommendedAsk && recommendedAsk !== 'Not Available' && (
                  <div className="relative rounded-2xl bg-white border border-slate-200/70 p-5 flex flex-col justify-between shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Recommended Ask</p>
                    <p className="font-black text-2xl text-slate-900 leading-tight">{recommendedAsk}</p>
                    <p className="text-slate-400 text-[10px] font-medium mt-2">AI-estimated donation target</p>
                  </div>
                )}
              </div>

              {/* ── Section 2: Narrative Lead Card ── */}
              {(aiData?.brief || aiData?.reasoning) && (
                <div className="bg-white border border-slate-200/70 rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
                  <div className="flex">
                    <div className="w-1 shrink-0 bg-gradient-to-b from-accent via-accent/70 to-accent/20" />
                    <div className="flex-1 p-6 space-y-4">
                      {aiData?.brief && aiData.brief !== 'Not found' && (
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-accent mb-2 flex items-center gap-1.5">
                            <Newspaper size={12} weight="bold" /> Summary
                          </p>
                          <p className="text-slate-800 text-[15px] font-semibold leading-loose">{aiData.brief}</p>
                        </div>
                      )}
                      {aiData?.reasoning && aiData.reasoning !== 'Not found' && (
                        <div className="pt-3 border-t border-slate-100">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                            <Robot size={12} weight="bold" /> AI Assessment
                          </p>
                          <blockquote className="text-slate-600 text-sm italic leading-relaxed pl-3 border-l-2 border-slate-200">
                            {aiData.reasoning}
                          </blockquote>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Section 3: Two-Column Analytical Row ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print-page-3">

                {/* Financial Signals */}
                <SectionCard elevated>
                  <SectionTitle icon={<Coins size={20} className="text-emerald-600" weight="duotone" />} label="Financial Signals" />
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      {aiData?.estimated_donation_capacity && safeStr(aiData.estimated_donation_capacity) !== 'N/A' && (
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 mb-1">Donation Capacity</p>
                          <p className="text-slate-800 font-extrabold text-sm leading-snug">{formatCapacityValue(aiData.estimated_donation_capacity)}</p>
                        </div>
                      )}
                      {aiData?.current_earning && safeStr(aiData.current_earning) !== 'N/A' && (
                        <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-3.5">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Current Earnings</p>
                          <p className="text-slate-800 font-extrabold text-sm leading-snug">{formatCapacityValue(aiData.current_earning)}</p>
                        </div>
                      )}
                    </div>
                    {aiData?.alumni_interest_at_iit && (
                      <div className="flex items-center justify-between py-2 border-t border-slate-100">
                        <span className="text-xs font-medium text-slate-500">Alumni Interest at IIT</span>
                        <Badge val={aiData.alumni_interest_at_iit} />
                      </div>
                    )}
                    {aiData?.cash_earnings?.amount && aiData.cash_earnings.amount !== 'Not found' && (
                      <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/60 space-y-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Cash Earnings</span>
                        <p className="text-slate-800 font-semibold text-sm">{aiData.cash_earnings.amount}</p>
                        {aiData.cash_earnings.source && <p className="text-slate-400 text-[11px]">Source: {aiData.cash_earnings.source}</p>}
                        {aiData.cash_earnings.note && <p className="text-slate-400 text-[11px] italic">{aiData.cash_earnings.note}</p>}
                      </div>
                    )}
                    {aiData?.stock_holdings?.amount && aiData.stock_holdings.amount !== 'Not found' && (
                      <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/60 space-y-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Stock Holdings</span>
                        <p className="text-slate-800 font-semibold text-sm">{aiData.stock_holdings.amount}</p>
                        {aiData.stock_holdings.source && <p className="text-slate-400 text-[11px]">Source: {aiData.stock_holdings.source}</p>}
                        {aiData.stock_holdings.note && <p className="text-slate-400 text-[11px] italic">{aiData.stock_holdings.note}</p>}
                      </div>
                    )}
                    {aiData?.estimated_donation_capacity_reasoning && aiData.estimated_donation_capacity_reasoning !== 'Not found' && (
                      <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-100 rounded-xl p-3.5">
                        <Info size={15} className="text-amber-500 shrink-0 mt-0.5" weight="fill" />
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-1">Capacity Reasoning</p>
                          <p className="text-slate-700 text-xs font-medium leading-relaxed">{aiData.estimated_donation_capacity_reasoning}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </SectionCard>

                {/* Career Trajectory */}
                {ct && (
                  <SectionCard>
                    <div className="flex items-start justify-between gap-3 mb-5">
                      <SectionTitle icon={<TrendUp size={20} className="text-sky-600" weight="duotone" />} label="Career Trajectory" />
                      <div className="flex flex-col items-end gap-1.5 -mt-1 shrink-0">
                        {ct.trajectory_direction && ct.trajectory_direction !== 'Not found' && (
                          <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-full ${trajectoryColor(ct.trajectory_direction)}`}>
                            {ct.trajectory_direction}
                          </span>
                        )}
                        {ct.confidence && ct.confidence !== 'Not found' && (
                          <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-full border ${badgeColor(ct.confidence)}`}>
                            {ct.confidence} Confidence
                          </span>
                        )}
                      </div>
                    </div>
                    {ct.current_position && ct.current_position !== 'Not found' && (
                      <div className="flex items-start gap-2.5 bg-sky-50 border border-sky-100 rounded-xl p-3.5 mb-4">
                        <Info size={15} className="text-sky-500 shrink-0 mt-0.5" weight="fill" />
                        <p className="text-slate-700 text-xs font-medium leading-relaxed">{ct.current_position}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2.5 mb-4">
                      {[
                        { label: 'CXO Potential', value: ct.cxo_potential },
                        { label: 'Founder Potential', value: ct.founder_potential },
                        { label: 'Industry Outlook', value: ct.industry_outlook },
                        { label: 'Future Wealth Impact', value: ct.future_wealth_impact },
                      ].map(({ label, value }) =>
                        value && value !== 'Not found' ? (
                          <div key={label} className="bg-slate-50 border border-slate-200/60 rounded-xl p-3">
                            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">{label}</p>
                            <p className="text-slate-700 text-xs font-semibold leading-snug">{value}</p>
                          </div>
                        ) : null
                      )}
                    </div>
                    {ct.key_signals && ct.key_signals.length > 0 && (
                      <div className="space-y-2 mb-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Key Signals</p>
                        <div className="space-y-1.5">
                          {ct.key_signals.map((sig, i) => (
                            <div key={i} className="pl-3 border-l-2 border-sky-400 text-slate-700 text-xs font-medium leading-relaxed py-1">
                              {sig}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {ct.predicted_roles && ct.predicted_roles.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Predicted Roles</p>
                        <div className="flex flex-wrap gap-1.5">
                          {ct.predicted_roles.map((role, i) => (
                            <span key={i} className="text-[10px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-2.5 py-1 rounded-full">
                              {role}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </SectionCard>
                )}
              </div>

              {/* ── Section 4: Intelligence Signals Card Grid ── */}
              {([
                aiData?.wealth_signals,
                aiData?.philanthropic_interests,
                aiData?.board_and_advisory,
                aiData?.media_and_profiles,
                aiData?.awards_and_recognition,
                aiData?.approach_for_cause,
                aiData?.interests,
              ] as (string | undefined)[]).some(v => v && v !== 'Not found') && (
                <div className="space-y-3 print-page-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Star size={16} className="text-amber-500" weight="duotone" />
                    <h3 className="font-bold text-base text-slate-800">Intelligence Signals</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {([
                      { label: 'Wealth Signals', value: aiData?.wealth_signals, icon: <TrendUp size={16} weight="duotone" />, bg: 'bg-emerald-50 border-emerald-100', iconColor: 'text-emerald-600' },
                      { label: 'Philanthropic Interests', value: aiData?.philanthropic_interests, icon: <Heart size={16} weight="duotone" />, bg: 'bg-rose-50 border-rose-100', iconColor: 'text-rose-500' },
                      { label: 'Board & Advisory', value: aiData?.board_and_advisory, icon: <UsersThree size={16} weight="duotone" />, bg: 'bg-violet-50 border-violet-100', iconColor: 'text-violet-600' },
                      { label: 'Media & Profiles', value: aiData?.media_and_profiles, icon: <Newspaper size={16} weight="duotone" />, bg: 'bg-sky-50 border-sky-100', iconColor: 'text-sky-600' },
                      { label: 'Awards & Recognition', value: aiData?.awards_and_recognition, icon: <Trophy size={16} weight="duotone" />, bg: 'bg-amber-50 border-amber-100', iconColor: 'text-amber-600' },
                      { label: 'Approach for Cause', value: aiData?.approach_for_cause, icon: <ThumbsUp size={16} weight="duotone" />, bg: 'bg-orange-50 border-orange-100', iconColor: 'text-accent' },
                      { label: 'Personal Interests', value: aiData?.interests, icon: <BookOpen size={16} weight="duotone" />, bg: 'bg-indigo-50 border-indigo-100', iconColor: 'text-indigo-600' },
                    ] as { label: string; value?: string; icon: React.ReactNode; bg: string; iconColor: string }[])
                      .filter(item => item.value && item.value !== 'Not found')
                      .map(item => (
                        <div key={item.label} className="bg-white border border-slate-200/70 rounded-2xl p-4 flex gap-3 items-start shadow-[0_1px_3px_rgba(15,23,42,0.05)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] transition-shadow">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${item.bg}`}>
                            <span className={item.iconColor}>{item.icon}</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{item.label}</p>
                            <p className="text-slate-700 text-sm font-medium leading-relaxed">{item.value}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* ── Strategic Fit + Data Notes ── */}
              {((aiData?.strategic_fit_score && aiData.strategic_fit_score !== 'Not found') ||
                (aiData?.data_correction_notes && aiData.data_correction_notes !== 'No discrepancies')) && (
                <div className="flex flex-wrap gap-3">
                  {aiData?.strategic_fit_score && aiData.strategic_fit_score !== 'Not found' && (
                    <div className="flex items-center gap-2 bg-white border border-slate-200/70 rounded-xl px-4 py-2.5 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
                      <TreeStructure size={14} className="text-slate-500 shrink-0" />
                      <span className="text-xs font-medium text-slate-500">Strategic Fit:</span>
                      <span className="font-bold text-slate-800 text-sm">{aiData.strategic_fit_score}</span>
                    </div>
                  )}
                  {aiData?.data_correction_notes && aiData.data_correction_notes !== 'No discrepancies' && (
                    <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3 flex-1 min-w-[200px]">
                      <Warning size={15} className="text-amber-500 shrink-0 mt-0.5" weight="fill" />
                      <p className="text-amber-800 text-xs font-medium leading-relaxed">{aiData.data_correction_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ── Citations — collapsed at bottom ── */}
              {aiMeta?.citations && aiMeta.citations.length > 0 && (
                <details className="group no-print">
                  <summary className="cursor-pointer list-none flex items-center gap-2 text-[11px] font-bold text-slate-400 hover:text-slate-600 transition-colors select-none py-1">
                    <Link size={13} />
                    <span>Show Sources ({aiMeta.citations.length})</span>
                    <span className="ml-auto text-slate-300 group-open:rotate-180 transition-transform duration-200">▾</span>
                  </summary>
                  <div className="flex flex-wrap gap-2 mt-3 pl-1">
                    {aiMeta.citations.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                        className="text-[11px] font-semibold text-slate-500 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-2.5 py-1.5 rounded-lg truncate max-w-[280px] transition-colors flex items-center gap-1"
                        title={url}>
                        {new URL(url).hostname.replace('www.', '')} <ArrowSquareOut size={11} weight="bold" />
                      </a>
                    ))}
                  </div>
                </details>
              )}

            </div>
          )}

          {/* ── VALS Framework Section ── */}
          <ValsSection
            alumni={alumni}
            onUpdateAlumni={(data) => setAlumni((prev) => prev ? { ...prev, vals_framework: data } : null)}
          />

          {/* ── Financial Capacity ── */}
          <SectionCard>
            <SectionTitle icon={<Coins size={22} className="text-emerald-600" weight="duotone" />} label="Financial Capacity" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
              <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-4">
                <p className="text-xs font-medium text-slate-500 mb-2">Estimated Net Worth</p>
                <p className="font-bold text-lg text-slate-900 leading-tight">{estimatedNetWorth}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-4">
                <p className="text-xs font-medium text-slate-500 mb-2">Recommended Ask</p>
                <p className="font-bold text-lg text-slate-900 leading-tight">{recommendedAsk}</p>
              </div>
            </div>
          </SectionCard>



        </div>
      )}



      {activeTab === 'network' && (
        <div className="space-y-6 animate-in fade-in duration-300 pt-1">
          {similarDonorsSection}
        </div>
      )}

    </div>

      {/* ── PDF Print Root ── rendered off-screen so html2canvas can capture it */}
      {isPrinting && (
        <div
          ref={pdfRootRef}
          id="pdf-print-root"
          className="font-sans space-y-8 bg-white text-slate-900 pb-16"
          style={{ position: 'absolute', left: '-9999px', top: 0, width: '900px', zIndex: -1 }}
        >
          {/* Header */}
          <div className="border-b border-slate-200 pb-6 mb-8 flex justify-between items-end">
            <div>
              <p className="text-xs font-bold text-accent tracking-widest uppercase mb-1">IIT Madras Alumni Intelligence Report</p>
              <h1 className="text-3xl font-black text-slate-900 m-0 leading-tight">{recordsName}</h1>
              <div className="flex gap-4 text-sm font-semibold text-slate-500 mt-2">
                <span>ID: {safeStr(alumniId)}</span>
                {sInfo?.department && <span>{sInfo.department}</span>}
                {sInfo?.batch && <span>Batch {sInfo.batch}</span>}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-slate-400">Generated</p>
              <p className="text-sm font-bold text-slate-700">{new Date().toLocaleDateString()}</p>
            </div>
          </div>

          {/* AI Research Section */}
          {hasResearch && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold border-b border-slate-200 pb-2 mb-4">AI Research & Signals</h2>
              
              {/* Score Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Donor Score</p>
                  <p className="font-black text-2xl text-slate-900">{aiData?.potential_donor_score ? `${scoreWidth(aiData.potential_donor_score)}/100` : 'N/A'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Readiness</p>
                  <p className="font-black text-2xl text-slate-900">{aiData?.readiness_to_give || 'N/A'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Ask Target</p>
                  <p className="font-black text-2xl text-slate-900">{recommendedAsk !== 'Not Available' ? recommendedAsk : (aiData?.targeted_donation_amount || 'N/A')}</p>
                </div>
              </div>

              {/* Executive Summary */}
              {aiData?.brief && aiData.brief !== 'Not found' && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5">
                    <Newspaper size={14} weight="bold" className="text-accent" /> Executive Summary
                  </h3>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{aiData.brief}</p>
                </div>
              )}

              {/* Platform Reasoning */}
              {aiData?.reasoning && aiData.reasoning !== 'Not found' && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5">
                    <Robot size={14} weight="bold" className="text-slate-500" /> Platform Reasoning
                  </h3>
                  <p className="text-sm text-slate-600 italic leading-relaxed whitespace-pre-wrap pl-3 border-l-2 border-slate-200">{aiData.reasoning}</p>
                </div>
              )}

              {/* Analytical Row: Financial Signals & Career Trajectory */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Financial Signals */}
                {(aiData?.estimated_donation_capacity || aiData?.current_earning || aiData?.alumni_interest_at_iit || aiData?.cash_earnings?.amount || aiData?.stock_holdings?.amount) && (
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5">
                      <Coins size={16} className="text-emerald-600" weight="duotone" /> Financial Signals
                    </h3>
                    <div className="space-y-2 text-xs text-slate-700">
                      {aiData?.estimated_donation_capacity && safeStr(aiData.estimated_donation_capacity) !== 'N/A' && (
                        <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                          <span className="text-slate-500">Donation Capacity:</span>
                          <span className="font-bold text-slate-800">{formatCapacityValue(aiData.estimated_donation_capacity)}</span>
                        </div>
                      )}
                      {aiData?.current_earning && safeStr(aiData.current_earning) !== 'N/A' && (
                        <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                          <span className="text-slate-500">Current Earnings:</span>
                          <span className="font-bold text-slate-800">{formatCapacityValue(aiData.current_earning)}</span>
                        </div>
                      )}
                      {aiData?.alumni_interest_at_iit && (
                        <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                          <span className="text-slate-500">Alumni Interest at IIT:</span>
                          <span className="font-bold text-slate-800">{aiData.alumni_interest_at_iit}</span>
                        </div>
                      )}
                      {aiData?.cash_earnings?.amount && aiData.cash_earnings.amount !== 'Not found' && (
                        <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                          <span className="text-slate-500">Cash Earnings:</span>
                          <span className="font-bold text-slate-800">{aiData.cash_earnings.amount}</span>
                        </div>
                      )}
                      {aiData?.stock_holdings?.amount && aiData.stock_holdings.amount !== 'Not found' && (
                        <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                          <span className="text-slate-500">Stock Holdings:</span>
                          <span className="font-bold text-slate-800">{aiData.stock_holdings.amount}</span>
                        </div>
                      )}
                      {aiData?.estimated_donation_capacity_reasoning && aiData.estimated_donation_capacity_reasoning !== 'Not found' && (
                        <p className="text-[11px] text-slate-500 italic mt-1 pt-1 border-t border-slate-200/40">
                          {aiData.estimated_donation_capacity_reasoning}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Career Trajectory */}
                {ct && (
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5">
                      <TrendUp size={16} className="text-sky-600" weight="duotone" /> Career Trajectory
                    </h3>
                    <div className="space-y-2 text-xs text-slate-700">
                      {ct.current_position && ct.current_position !== 'Not found' && (
                        <p className="font-medium text-slate-800 pb-1.5 border-b border-slate-200/60">{ct.current_position}</p>
                      )}
                      {ct.trajectory_direction && ct.trajectory_direction !== 'Not found' && (
                        <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                          <span className="text-slate-500">Direction:</span>
                          <span className="font-bold text-slate-800">{ct.trajectory_direction} {ct.confidence && ct.confidence !== 'Not found' ? `(${ct.confidence})` : ''}</span>
                        </div>
                      )}
                      {ct.industry_outlook && ct.industry_outlook !== 'Not found' && (
                        <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                          <span className="text-slate-500">Industry Outlook:</span>
                          <span className="font-bold text-slate-800">{ct.industry_outlook}</span>
                        </div>
                      )}
                      {ct.cxo_potential && ct.cxo_potential !== 'Not found' && (
                        <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                          <span className="text-slate-500">CXO Potential:</span>
                          <span className="font-bold text-slate-800">{ct.cxo_potential}</span>
                        </div>
                      )}
                      {ct.future_wealth_impact && ct.future_wealth_impact !== 'Not found' && (
                        <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                          <span className="text-slate-500">Wealth Impact:</span>
                          <span className="font-bold text-slate-800">{ct.future_wealth_impact}</span>
                        </div>
                      )}
                      {ct.key_signals && ct.key_signals.length > 0 && (
                        <div className="pt-2">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Key Signals</p>
                          <div className="space-y-1">
                            {ct.key_signals.map((sig, i) => (
                              <div key={i} className="pl-2.5 border-l-2 border-sky-400 text-slate-700 text-[11px] leading-relaxed py-0.5">
                                {sig}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Intelligence Signals Section ── */}
              {([
                aiData?.wealth_signals,
                aiData?.philanthropic_interests,
                aiData?.board_and_advisory,
                aiData?.media_and_profiles,
                aiData?.awards_and_recognition,
                aiData?.approach_for_cause,
                aiData?.interests,
              ] as (string | undefined)[]).some(v => v && v !== 'Not found') && (
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Star size={18} className="text-amber-500" weight="duotone" />
                    <h3 className="font-bold text-base text-slate-800">Intelligence Signals</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {([
                      { label: 'Wealth Signals', value: aiData?.wealth_signals, icon: <TrendUp size={16} weight="duotone" />, bg: 'bg-emerald-50 border-emerald-100', iconColor: 'text-emerald-600' },
                      { label: 'Philanthropic Interests', value: aiData?.philanthropic_interests, icon: <Heart size={16} weight="duotone" />, bg: 'bg-rose-50 border-rose-100', iconColor: 'text-rose-500' },
                      { label: 'Board & Advisory', value: aiData?.board_and_advisory, icon: <UsersThree size={16} weight="duotone" />, bg: 'bg-violet-50 border-violet-100', iconColor: 'text-violet-600' },
                      { label: 'Media & Profiles', value: aiData?.media_and_profiles, icon: <Newspaper size={16} weight="duotone" />, bg: 'bg-sky-50 border-sky-100', iconColor: 'text-sky-600' },
                      { label: 'Awards & Recognition', value: aiData?.awards_and_recognition, icon: <Trophy size={16} weight="duotone" />, bg: 'bg-amber-50 border-amber-100', iconColor: 'text-amber-600' },
                      { label: 'Approach for Cause', value: aiData?.approach_for_cause, icon: <ThumbsUp size={16} weight="duotone" />, bg: 'bg-orange-50 border-orange-100', iconColor: 'text-accent' },
                      { label: 'Personal Interests', value: aiData?.interests, icon: <BookOpen size={16} weight="duotone" />, bg: 'bg-indigo-50 border-indigo-100', iconColor: 'text-indigo-600' },
                    ] as { label: string; value?: string; icon: React.ReactNode; bg: string; iconColor: string }[])
                      .filter(item => item.value && item.value !== 'Not found')
                      .map(item => (
                        <div key={item.label} className="bg-white border border-slate-200 rounded-xl p-3.5 flex gap-3 items-start shadow-sm">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${item.bg}`}>
                            <span className={item.iconColor}>{item.icon}</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{item.label}</p>
                            <p className="text-slate-700 text-xs font-medium leading-relaxed">{item.value}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Strategic Fit + Data Notes */}
              {((aiData?.strategic_fit_score && aiData.strategic_fit_score !== 'Not found') ||
                (aiData?.data_correction_notes && aiData.data_correction_notes !== 'No discrepancies')) && (
                <div className="flex flex-wrap gap-3 mb-6">
                  {aiData?.strategic_fit_score && aiData.strategic_fit_score !== 'Not found' && (
                    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                      <TreeStructure size={14} className="text-slate-500 shrink-0" />
                      <span className="text-xs font-medium text-slate-500">Strategic Fit:</span>
                      <span className="font-bold text-slate-800 text-sm">{aiData.strategic_fit_score}</span>
                    </div>
                  )}
                  {aiData?.data_correction_notes && aiData.data_correction_notes !== 'No discrepancies' && (
                    <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3 flex-1 min-w-[200px]">
                      <Warning size={15} className="text-amber-500 shrink-0 mt-0.5" weight="fill" />
                      <p className="text-amber-800 text-xs font-medium leading-relaxed">{aiData.data_correction_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* VALS Section */}
          {alumni?.vals_framework && (
            <div className="space-y-6 pt-6 border-t border-slate-200" style={{ pageBreakInside: 'avoid' }}>
              <h2 className="text-xl font-bold border-b border-slate-200 pb-2 mb-4">Psychographic VALS Profile</h2>
              
              <div className="grid grid-cols-2 gap-8 mb-6">
                <div>
                  <h3 className="text-sm font-bold text-slate-700 mb-3">Core Motivations</h3>
                  <div className="space-y-2">
                    {Object.entries((alumni.vals_framework as any).scores || {}).map(([key, val]: [string, any]) => (
                      <div key={key} className="flex justify-between items-center text-sm border-b border-slate-100 pb-1">
                        <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-bold">{val?.score || 0}/10</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Giving Propensity</p>
                    <p className="font-black text-2xl text-slate-900">{(alumni.vals_framework as any).donation_propensity || 0}%</p>
                  </div>
                  
                  <div>
                    <p className="text-xs font-bold text-slate-500 mb-1">Primary Motivation</p>
                    <p className="text-sm font-semibold">{(alumni.vals_framework as any).primary_motivation || 'General Philanthropy'}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs font-bold text-slate-500 mb-1">Outreach Strategy</p>
                    <p className="text-sm font-semibold">{(alumni.vals_framework as any).best_outreach_strategy || 'Relationship Building'}</p>
                  </div>
                </div>
              </div>

              {(alumni.vals_framework as any).unknowns_or_risks && (
                <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-xl">
                  <h3 className="text-sm font-bold text-rose-800 mb-1">Risks & Unknowns</h3>
                  <p className="text-sm text-rose-700">{(alumni.vals_framework as any).unknowns_or_risks}</p>
                </div>
              )}

              {/* Financial Capacity */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="p-4 border border-slate-200 rounded-xl">
                  <p className="text-xs font-bold text-slate-500 mb-1">Estimated Net Worth</p>
                  <p className="font-bold text-lg text-slate-900">{estimatedNetWorth}</p>
                </div>
                <div className="p-4 border border-slate-200 rounded-xl">
                  <p className="text-xs font-bold text-slate-500 mb-1">Recommended Ask</p>
                  <p className="font-bold text-lg text-slate-900">{recommendedAsk}</p>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      <ResearchHistorySheet
        alumniId={historyOpen ? alumni._id : null}
        name={recordsName}
        onClose={() => setHistoryOpen(false)}
        linkToProfile={false}
      />
    </>
  );
}